import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FiArrowLeft, FiCalendar, FiCamera, FiCheckCircle, FiClock, FiCopy, FiEdit3, FiGlobe, FiInfo, FiLogIn, FiMapPin, FiMoreHorizontal, FiMoreVertical, FiNavigation, FiPhone, FiPlus, FiRefreshCw, FiSearch, FiSettings, FiStar, FiTrash2, FiUpload, FiUserPlus, FiX } from "react-icons/fi";
import { server } from "../server";
import Loader from "../components/Layout/Loader";
import ImageCropperModal from "../components/Image/ImageCropperModal";
import { setUser } from "../redux/actions/user";
import { cacheAuthUser, clearAuthSession, persistAuthToken } from "../utils/authSession";
import { setStoredCurrentProfile } from "../utils/shopProfile";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
  readImageFileAsDataUrl,
} from "../utils/imageUploadPreparation";

const EVENT_ID_STORAGE_KEY = "buzzvibe.le.eventId";
const GEOLOCATION_STORAGE_KEY = "buzzvibe.le.location";
const CALENDAR_INTENT_STORAGE_KEY = "gamebird.calendar.intent";
const DEFAULT_EVENT_ID = process.env.REACT_APP_LE_EVENT_ID || "GB1001";
const DEFAULT_AVATAR_URL = "https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/f9988cbf-518b-4a25-ed95-d26867fa8000/public";
const DEFAULT_BANNER_URL = "https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/6cc98ecc-5bda-4edd-1e30-1895bb0e5c00/public";
const EVENT_HERO_IMAGE_PREP_TIMEOUT_MS = 90000;
const EVENT_HERO_MAX_IMAGE_UPLOAD_BYTES = 12 * 1024 * 1024;
const LE_MAP_DEFAULT_CENTER = { lat: 34.0549, lng: -118.2426 };
const NEARBY_RADIUS_OPTIONS = [
  { value: "10", label: "10 mi", miles: 10 },
  { value: "25", label: "25 mi", miles: 25 },
  { value: "50", label: "50 mi", miles: 50 },
  { value: "100", label: "100+ mi", miles: Number.POSITIVE_INFINITY },
];
const DEFAULT_EVENT_DETAILS = {
  title: "GameBird Live Event",
  venue: "Event Location",
  venueAddress: "",
  venueLocation: "",
  venueCategory: "",
  venueRating: null,
  venueReviewCount: null,
  venueHours: "",
  venueWebsite: "",
  venuePhone: "",
  venueLatitude: "",
  venueLongitude: "",
  schedule: "Schedule pending",
  summary: "Open your event pass, then scan in at the physical location to play.",
};

const KNOWN_VENUE_PROFILES = [
  {
    match: /miami shooters club/i,
    venue: "Miami Shooters Club",
    venueAddress: "1425 SW 107th Ave, Miami, FL 33174",
    venueLocation: "Floor 1 - University Center North",
    venueCategory: "Firearms academy",
    venueRating: 4.9,
    venueReviewCount: 321,
    venueHours: "Open 24 hours",
    venueWebsite: "miamishootersclub.com",
    venuePhone: "(305) 551-0100",
    venueLatitude: 25.7558056,
    venueLongitude: -80.3665438,
  },
];

const normalizeEventId = (value = "") => {
  const normalizedEventId = String(value || DEFAULT_EVENT_ID)
    .trim()
    .replace(/[^a-z0-9]+/gi, "")
    .toUpperCase();

  return normalizedEventId || DEFAULT_EVENT_ID;
};

const createAlphanumericEventId = () => `GB${String(Date.now()).slice(-8)}`;

const getStoredEventId = () => {
  if (typeof window === "undefined") {
    return DEFAULT_EVENT_ID;
  }

  return window.sessionStorage.getItem(EVENT_ID_STORAGE_KEY) || DEFAULT_EVENT_ID;
};

const storeEventId = (eventId) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(EVENT_ID_STORAGE_KEY, normalizeEventId(eventId));
};

const useEventId = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const eventId = normalizeEventId(params.eventId || searchParams.get("eventId") || getStoredEventId());

  useEffect(() => {
    storeEventId(eventId);
  }, [eventId]);

  return eventId;
};

const eventPath = (path, eventId) => `${path}?eventId=${encodeURIComponent(normalizeEventId(eventId))}`;

const validateHandle = (handle) => /^[a-zA-Z0-9]{1,30}$/.test(handle || "");

const getEventDetails = (searchParams) => ({
  title: searchParams.get("title") || DEFAULT_EVENT_DETAILS.title,
  venue: searchParams.get("venue") || DEFAULT_EVENT_DETAILS.venue,
  venueAddress: searchParams.get("venueAddress") || DEFAULT_EVENT_DETAILS.venueAddress,
  venueLocation: searchParams.get("venueLocation") || DEFAULT_EVENT_DETAILS.venueLocation,
  venueCategory: searchParams.get("venueCategory") || DEFAULT_EVENT_DETAILS.venueCategory,
  venueRating: searchParams.get("venueRating") || DEFAULT_EVENT_DETAILS.venueRating,
  venueReviewCount: searchParams.get("venueReviewCount") || DEFAULT_EVENT_DETAILS.venueReviewCount,
  venueHours: searchParams.get("venueHours") || DEFAULT_EVENT_DETAILS.venueHours,
  venueWebsite: searchParams.get("venueWebsite") || DEFAULT_EVENT_DETAILS.venueWebsite,
  venuePhone: searchParams.get("venuePhone") || DEFAULT_EVENT_DETAILS.venuePhone,
  schedule: searchParams.get("schedule") || DEFAULT_EVENT_DETAILS.schedule,
  summary: searchParams.get("summary") || DEFAULT_EVENT_DETAILS.summary,
  start: searchParams.get("start") || "",
  end: searchParams.get("end") || "",
});

const getKnownVenueProfile = (eventRecord, fallbackDetails = DEFAULT_EVENT_DETAILS) => {
  const searchableText = [
    eventRecord?.venue,
    eventRecord?.title,
    fallbackDetails?.venue,
    fallbackDetails?.title,
  ].filter(Boolean).join(" ");

  return KNOWN_VENUE_PROFILES.find((profile) => profile.match.test(searchableText)) || {};
};

const getFirstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const mapLeEventToDetails = (eventRecord, fallbackDetails = DEFAULT_EVENT_DETAILS) => {
  const knownVenueProfile = getKnownVenueProfile(eventRecord, fallbackDetails);
  const venueLatitude = getFirstValue(
    typeof eventRecord?.venueLatitude === "number" ? eventRecord.venueLatitude : undefined,
    typeof eventRecord?.location?.lat === "number" ? eventRecord.location.lat : undefined,
    knownVenueProfile.venueLatitude,
    fallbackDetails.venueLatitude
  );
  const venueLongitude = getFirstValue(
    typeof eventRecord?.venueLongitude === "number" ? eventRecord.venueLongitude : undefined,
    typeof eventRecord?.location?.lng === "number" ? eventRecord.location.lng : undefined,
    knownVenueProfile.venueLongitude,
    fallbackDetails.venueLongitude
  );

  return {
    title: eventRecord?.title || fallbackDetails.title,
    venue: eventRecord?.venue || knownVenueProfile.venue || fallbackDetails.venue,
    venueAddress: getFirstValue(eventRecord?.venueAddress, knownVenueProfile.venueAddress, fallbackDetails.venueAddress, eventRecord?.location?.address, ""),
    venueLocation: getFirstValue(eventRecord?.venueLocation, knownVenueProfile.venueLocation, fallbackDetails.venueLocation, ""),
    venueCategory: getFirstValue(eventRecord?.venueCategory, knownVenueProfile.venueCategory, fallbackDetails.venueCategory, ""),
    venueRating: getFirstValue(eventRecord?.venueRating, knownVenueProfile.venueRating, fallbackDetails.venueRating, null),
    venueReviewCount: getFirstValue(eventRecord?.venueReviewCount, knownVenueProfile.venueReviewCount, fallbackDetails.venueReviewCount, null),
    venueHours: getFirstValue(eventRecord?.venueHours, knownVenueProfile.venueHours, fallbackDetails.venueHours, ""),
    venueWebsite: getFirstValue(eventRecord?.venueWebsite, knownVenueProfile.venueWebsite, fallbackDetails.venueWebsite, ""),
    venuePhone: getFirstValue(eventRecord?.venuePhone, knownVenueProfile.venuePhone, fallbackDetails.venuePhone, ""),
    schedule: eventRecord?.schedule || fallbackDetails.schedule || DEFAULT_EVENT_DETAILS.schedule,
    summary: eventRecord?.summary || fallbackDetails.summary,
    start: eventRecord?.startsAt || "",
    end: eventRecord?.endsAt || "",
    heroImageUrl: eventRecord?.heroImageUrl || DEFAULT_BANNER_URL,
    eventUrl: eventRecord?.eventUrl || "",
    eventQrCodeUrl: eventRecord?.eventQrCodeUrl || "",
    displayOnLe: eventRecord?.displayOnLe !== false,
    displayOnPrimary: eventRecord?.displayOnPrimary === true,
    venueLatitude,
    venueLongitude,
    attendeeCount: eventRecord?.attendeeCount || 0,
    isAttending: eventRecord?.isAttending === true,
  };
};

const toCalendarDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
};

const getEventLocationLabel = (details = {}) => (
  [details.venue, details.venueAddress].filter(Boolean).join(" - ") || details.venue || ""
);

const buildCalendarHref = (details) => {
  const start = toCalendarDate(details.start);
  const end = toCalendarDate(details.end);
  const datesLine = start ? `DTSTART:${start}\n${end ? `DTEND:${end}\n` : ""}` : "";
  const description = [details.summary, details.eventUrl ? `Event: ${details.eventUrl}` : ""].filter(Boolean).join("\n");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//GameBird//Events//EN",
    "BEGIN:VEVENT",
    `SUMMARY:${details.title}`,
    `LOCATION:${getEventLocationLabel(details)}`,
    `DESCRIPTION:${description}`,
    datesLine.trim(),
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\n");

  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
};

const getCalendarRange = (details) => {
  const startDate = details.start ? new Date(details.start) : null;
  const safeStartDate = startDate && !Number.isNaN(startDate.getTime()) ? startDate : null;
  const endDate = details.end ? new Date(details.end) : null;
  const safeEndDate = endDate && !Number.isNaN(endDate.getTime()) ? endDate : null;

  if (!safeStartDate) {
    return { start: "", end: "" };
  }

  return {
    start: safeStartDate.toISOString(),
    end: (safeEndDate || new Date(safeStartDate.getTime() + 2 * 60 * 60 * 1000)).toISOString(),
  };
};

const toGoogleCalendarDate = (value) => value ? value.replace(/-|:|\.\d{3}/g, "") : "";

const buildGoogleCalendarHref = (details) => {
  const range = getCalendarRange(details);
  const dates = range.start ? `${toGoogleCalendarDate(range.start)}/${toGoogleCalendarDate(range.end)}` : "";
  const description = [details.summary, details.eventUrl ? `Event: ${details.eventUrl}` : ""].filter(Boolean).join("\n\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: details.title || DEFAULT_EVENT_DETAILS.title,
    details: description,
    location: getEventLocationLabel(details),
    trp: "false",
    sf: "true",
    output: "xml",
  });

  if (dates) {
    params.set("dates", dates);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildOutlookCalendarHref = (details) => {
  const range = getCalendarRange(details);
  const description = [details.summary, details.eventUrl ? `Event: ${details.eventUrl}` : ""].filter(Boolean).join("\n\n");
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    subject: details.title || DEFAULT_EVENT_DETAILS.title,
    body: description,
    location: getEventLocationLabel(details),
  });

  if (range.start) {
    params.set("startdt", range.start.replace(/\.\d{3}Z$/, "Z"));
    params.set("enddt", range.end.replace(/\.\d{3}Z$/, "Z"));
  }

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

const cacheCalendarIntent = (provider, details, eventId) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(CALENDAR_INTENT_STORAGE_KEY, JSON.stringify({
    provider,
    eventId: normalizeEventId(eventId),
    title: details.title || DEFAULT_EVENT_DETAILS.title,
    summary: details.summary || "",
    venue: details.venue || "",
    venueAddress: details.venueAddress || "",
    start: details.start || "",
    end: details.end || "",
    cachedAt: new Date().toISOString(),
  }));
};

const getStoredUserLocation = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const parsedLocation = JSON.parse(window.sessionStorage.getItem(GEOLOCATION_STORAGE_KEY) || "null");
    return typeof parsedLocation?.latitude === "number" && typeof parsedLocation?.longitude === "number"
      ? parsedLocation
      : null;
  } catch (error) {
    return null;
  }
};

const storeUserLocation = (location) => {
  if (typeof window === "undefined" || !location) {
    return;
  }

  window.sessionStorage.setItem(GEOLOCATION_STORAGE_KEY, JSON.stringify(location));
};

const clearStoredUserLocation = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(GEOLOCATION_STORAGE_KEY);
};

const getGeolocationErrorMessage = (error) => {
  if (error?.code === 1) {
    return "Location was not granted by the browser. Allow location for this site, then tap Use Location again.";
  }

  if (error?.code === 2) {
    return "Location is temporarily unavailable. Try again from a stronger signal.";
  }

  if (error?.code === 3) {
    return "Location timed out. Try again.";
  }

  return "Location is unavailable right now.";
};

const getEventCoordinates = (eventRecord) => {
  const details = mapLeEventToDetails(eventRecord);
  const latitude = Number(details.venueLatitude);
  const longitude = Number(details.venueLongitude);

  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

const calculateDistanceMiles = (fromLocation, toLocation) => {
  if (!fromLocation || !toLocation) {
    return null;
  }

  const earthRadiusMiles = 3958.8;
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const latitudeDelta = toRadians(toLocation.latitude - fromLocation.latitude);
  const longitudeDelta = toRadians(toLocation.longitude - fromLocation.longitude);
  const fromLatitude = toRadians(fromLocation.latitude);
  const toLatitude = toRadians(toLocation.latitude);
  const halfChord = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(halfChord), Math.sqrt(1 - halfChord));
};

const getEventTimeValue = (eventRecord, field = "start") => {
  const details = mapLeEventToDetails(eventRecord);
  const value = field === "end" ? details.end || details.start : details.start || details.end;
  const date = value ? new Date(value) : null;

  return date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
};

const isPastLeEvent = (eventRecord, now = Date.now()) => {
  const eventTime = getEventTimeValue(eventRecord, "end");
  return typeof eventTime === "number" && eventTime < now;
};

const filterPastEvents = (events, filterValue) => {
  if (filterValue === "30") {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return events.filter((eventRecord) => {
      const eventTime = getEventTimeValue(eventRecord);
      return typeof eventTime === "number" && eventTime >= cutoff;
    });
  }

  if (filterValue === "year") {
    const currentYear = new Date().getFullYear();
    return events.filter((eventRecord) => {
      const eventTime = getEventTimeValue(eventRecord);
      return typeof eventTime === "number" && new Date(eventTime).getFullYear() === currentYear;
    });
  }

  return events;
};

const getVenueCoordinates = (venue) => {
  const latitude = Number(venue?.latitude);
  const longitude = Number(venue?.longitude);

  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

const getClosestVenue = (eventRecord, venues, userLocation) => {
  const eventCoordinates = getEventCoordinates(eventRecord) || userLocation;

  if (!eventCoordinates || !Array.isArray(venues) || !venues.length) {
    return null;
  }

  return venues
    .map((venue) => ({
      ...venue,
      distanceMiles: calculateDistanceMiles(eventCoordinates, getVenueCoordinates(venue)),
    }))
    .filter((venue) => typeof venue.distanceMiles === "number")
    .sort((firstVenue, secondVenue) => firstVenue.distanceMiles - secondVenue.distanceMiles)[0] || null;
};

const getVenueAddressText = (venue = {}) => {
  const safeVenue = venue || {};
  const address = safeVenue.address || safeVenue.shopAddress || safeVenue.location?.address;

  if (typeof address === "string") {
    return address;
  }

  if (address && typeof address === "object") {
    return address.formattedAddress
      || address.fullAddress
      || [address.street, address.city, address.state, address.zipCode || address.postalCode].filter(Boolean).join(", ");
  }

  return [safeVenue.city, safeVenue.state].filter(Boolean).join(", ");
};

const getLeMapCoordinates = (eventRecord, venues, userLocation, index = 0) => {
  const eventCoordinates = getEventCoordinates(eventRecord);

  if (eventCoordinates) {
    return { ...eventCoordinates, source: "event" };
  }

  const closestVenue = getClosestVenue(eventRecord, venues, userLocation);
  const venueCoordinates = getVenueCoordinates(closestVenue);

  if (venueCoordinates) {
    const offset = index * 0.0006;
    return {
      latitude: venueCoordinates.latitude + offset,
      longitude: venueCoordinates.longitude + offset,
      source: "venue",
    };
  }

  if (userLocation) {
    const offset = index * 0.0008;
    return {
      latitude: userLocation.latitude + offset,
      longitude: userLocation.longitude + offset,
      source: "user",
    };
  }

  return null;
};

const formatEventDateLabel = (value) => {
  if (!value) {
    return "Schedule pending";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Schedule pending";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getEventScheduleLabel = (details = {}) => {
  const startLabel = details.start ? formatEventDateLabel(details.start) : "";
  const endLabel = details.end ? formatEventDateLabel(details.end) : "";

  if (startLabel && endLabel && startLabel !== endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  return startLabel || details.schedule || DEFAULT_EVENT_DETAILS.schedule;
};

const getDetailsCoordinates = (details = {}) => {
  const latitude = Number(details.venueLatitude);
  const longitude = Number(details.venueLongitude);

  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

const getMapQueryForDetails = (details = {}) => {
  const coordinates = getDetailsCoordinates(details);

  if (coordinates) {
    return `${coordinates.latitude},${coordinates.longitude}`;
  }

  return [details.venue, details.venueAddress].filter(Boolean).join(", ");
};

const buildGoogleMapsEmbedUrl = (details = {}, zoom = 15) => {
  const query = getMapQueryForDetails(details);
  return query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed` : "";
};

const buildGoogleMapsDirectionsUrl = (details = {}) => {
  const query = getMapQueryForDetails(details);
  return query ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}` : "";
};

const EventMapEmbed = ({ details, title = "Event location map", zoom = 15 }) => {
  const embedUrl = buildGoogleMapsEmbedUrl(details, zoom);

  if (!embedUrl) {
    return (
      <div className="event-le-map-fallback">
        <FiMapPin />
        <strong>Map location pending</strong>
        <p>Add an event address or coordinates to show this map.</p>
      </div>
    );
  }

  return (
    <iframe
      className="event-le-map-embed"
      title={title}
      src={embedUrl}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  );
};

const normalizeWebsiteHref = (value = "") => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) {
    return "";
  }

  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
};

const formatVenueRating = (rating, reviewCount) => {
  const numericRating = Number(rating);
  if (!Number.isFinite(numericRating)) {
    return "";
  }

  const reviews = Number(reviewCount);
  return Number.isFinite(reviews) && reviews > 0
    ? `${numericRating.toFixed(1)} (${Math.round(reviews)})`
    : numericRating.toFixed(1);
};

const EventVenueFact = ({ icon, label, value, href }) => {
  if (!value) {
    return null;
  }

  const content = href ? (
    <a href={href} target="_blank" rel="noreferrer">{value}</a>
  ) : <strong>{value}</strong>;

  return (
    <div className="event-le-venue-fact">
      <span className="event-le-venue-fact-icon">{icon}</span>
      <span className="event-le-venue-fact-copy">
        <small>{label}</small>
        {content}
      </span>
    </div>
  );
};

const EventVenueInfoCard = ({ details, title = "Club Details", actions = null }) => {
  const coordinates = getDetailsCoordinates(details);
  const rating = formatVenueRating(details.venueRating, details.venueReviewCount);
  const websiteHref = normalizeWebsiteHref(details.venueWebsite);
  const hasVenueDetails = Boolean(
    rating
    || details.venueCategory
    || details.venueAddress
    || details.venueLocation
    || details.venueHours
    || details.venueWebsite
    || details.venuePhone
    || coordinates
  );

  if (!hasVenueDetails) {
    return null;
  }

  return (
    <div className="event-le-venue-profile-card">
      <div className="event-le-venue-profile-heading">
        <span><FiInfo /> {title}</span>
        <h3>{details.venue || "Event Location"}</h3>
      </div>
      <div className="event-le-venue-fact-list">
        <EventVenueFact icon={<FiStar />} label="Rating" value={rating} />
        <EventVenueFact icon={<FiInfo />} label="Overview" value={details.venueCategory} />
        <EventVenueFact icon={<FiMapPin />} label="Address" value={details.venueAddress} />
        <EventVenueFact icon={<FiNavigation />} label="Location" value={details.venueLocation} />
        <EventVenueFact icon={<FiClock />} label="Hours" value={details.venueHours} />
        <EventVenueFact icon={<FiGlobe />} label="Website" value={details.venueWebsite} href={websiteHref} />
        <EventVenueFact icon={<FiPhone />} label="Call" value={details.venuePhone} href={details.venuePhone ? `tel:${details.venuePhone.replace(/[^+\d]/g, "")}` : ""} />
        <EventVenueFact
          icon={<FiMapPin />}
          label="Coordinates"
          value={coordinates ? `Latitude: ${coordinates.latitude} Longitude: ${coordinates.longitude}` : ""}
        />
      </div>
      {actions ? <div className="event-le-venue-actions">{actions}</div> : null}
    </div>
  );
};

const EventDetailVenueMap = ({ details, eventId, onOpenMaps, onOpenClub }) => {
  const coordinates = getDetailsCoordinates(details);
  const directionsUrl = buildGoogleMapsDirectionsUrl(details);

  if (!coordinates && !details.venueAddress) {
    return null;
  }

  return (
    <section className="event-le-detail-map-card">
      <div className="event-le-detail-section-title">
        <span><FiMapPin /> Event Location</span>
        <h2>{details.venue || "Event Location"}</h2>
      </div>

      <div className="event-le-detail-map-block" onClick={onOpenMaps} role="button" tabIndex={0} onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenMaps();
        }
      }}>
        <EventMapEmbed details={details} title={`${details.venue || eventId} map`} zoom={15} />
      </div>

      <EventVenueInfoCard
        details={details}
        title="Club Details"
        actions={(
          <>
            <button type="button" className="event-le-secondary-action" onClick={onOpenClub}>
              <FiInfo /> Club Details
            </button>
            {directionsUrl ? (
              <a className="event-le-primary-action" href={directionsUrl} target="_blank" rel="noreferrer">
                <FiNavigation /> Directions
              </a>
            ) : null}
            <button type="button" className="event-le-secondary-action" onClick={onOpenMaps}>
              <FiMapPin /> View Map
            </button>
          </>
        )}
      />
    </section>
  );
};

const mergeEventRecord = (events, nextEvent) => {
  if (!nextEvent?.eventId) {
    return events;
  }

  const nextEventId = normalizeEventId(nextEvent.eventId);
  const hasEvent = events.some((eventRecord) => normalizeEventId(eventRecord.eventId) === nextEventId);

  if (!hasEvent) {
    return [nextEvent, ...events];
  }

  return events.map((eventRecord) => (
    normalizeEventId(eventRecord.eventId) === nextEventId ? nextEvent : eventRecord
  ));
};

const EventShell = ({ children, eyebrow = "GameBird", title, subtitle }) => {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = title ? `${title} | GameBird` : "GameBird";
    }
  }, [title]);

  return (
    <div className="event-le-page">
      <main className="event-le-phone-shell">
        <section className="event-le-header">
          <span className="event-le-eyebrow">{eyebrow}</span>
          {title ? <h1>{title}</h1> : null}
          {subtitle ? <p>{subtitle}</p> : null}
        </section>
        {children}
      </main>
    </div>
  );
};

const EventProtectedRoute = ({ children }) => {
  const eventId = useEventId();
  const { loading, isAuthenticated } = useSelector((state) => state.user);

  if (loading !== false) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to={eventPath("/event/login", eventId)} replace />;
  }

  return children;
};

const EventTopTabs = ({ activeTab, eventId, localTabs = [], onLocalTab }) => {
  const navigate = useNavigate();

  const handleTabClick = (tab) => {
    if (localTabs.includes(tab) && onLocalTab) {
      onLocalTab(tab);
      return;
    }

    if (tab === "pass") {
      navigate(eventPath("/event/pass", eventId));
      return;
    }

    if (tab === "events") {
      navigate(eventPath("/event", eventId));
      return;
    }

    if (tab === "maps") {
      navigate(`${eventPath("/event", eventId)}&tab=maps`);
      return;
    }

    if (tab === "about") {
      navigate(`${eventPath("/event/pass", eventId)}&tab=about`);
      return;
    }

    navigate(`${eventPath("/event/pass", eventId)}&tab=account`);
  };

  return (
    <div className="event-le-tabs event-le-primary-tabs" role="tablist" aria-label="GameBird sections">
      {[
        ["pass", "Your Pass"],
        ["events", "Events"],
        ["maps", "Maps"],
        ["about", "About"],
        ["account", "Account"],
      ].map(([tab, label]) => (
        <button
          key={tab}
          type="button"
          className={activeTab === tab ? "active" : ""}
          onClick={() => handleTabClick(tab)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const EventPrimaryNavCard = ({ activeTab, eventId, localTabs = [], onLocalTab }) => (
  <section className="event-le-card event-le-directory-card event-le-detail-nav-card">
    <EventTopTabs activeTab={activeTab} eventId={eventId} localTabs={localTabs} onLocalTab={onLocalTab} />
  </section>
);

const EventProcessTitle = ({ icon, eyebrow, title, text }) => (
  <div className="event-le-process-title">
    <span>{icon}{eyebrow}</span>
    <strong>{title}</strong>
    {text ? <p>{text}</p> : null}
  </div>
);

const EventCalendarActions = ({ details, eventId, compact = false }) => {
  const calendarHref = buildCalendarHref(details);
  const calendarActions = [
    ["google", "Google", buildGoogleCalendarHref(details), { target: "_blank", rel: "noopener noreferrer" }],
    ["outlook", "Outlook", buildOutlookCalendarHref(details), { target: "_blank", rel: "noopener noreferrer" }],
    ["ics", "ICS", calendarHref, { download: `${normalizeEventId(eventId)}.ics` }],
  ];

  return (
    <div className={`event-le-calendar-group ${compact ? "compact" : ""}`}>
      <span className="event-le-mini-label"><FiCalendar /> Add to Calendar</span>
      <div className={`event-le-calendar-actions ${compact ? "compact" : ""}`}>
        {calendarActions.map(([provider, label, href, attributes]) => (
          <a
            key={provider}
            href={href}
            onClick={() => cacheCalendarIntent(provider, details, eventId)}
            {...attributes}
          >
            <FiCalendar /> {label}
          </a>
        ))}
      </div>
    </div>
  );
};

const EventBentoCard = ({
  eventRecord,
  isAdmin,
  isProcessing,
  isMenuOpen,
  showMenu,
  onAttend,
  onRemove,
  onManage,
  onOpen,
  onSelectLocation,
  onToggleMenu,
}) => {
  const eventId = normalizeEventId(eventRecord.eventId);
  const details = mapLeEventToDetails(eventRecord);
  const scheduleLabel = getEventScheduleLabel(details);

  return (
    <article className={`event-le-bento ${eventRecord.isAttending ? "is-going" : ""}`}>
      {showMenu ? (
        <div className="event-le-bento-menu-wrap">
          <button
            type="button"
            className="event-le-icon-action"
            aria-label="Event options"
            aria-expanded={isMenuOpen}
            onClick={() => onToggleMenu(isMenuOpen ? "" : eventId)}
          >
            <FiMoreHorizontal />
          </button>

          {isMenuOpen ? (
            <div className="event-le-bento-menu">
              <button type="button" onClick={() => onOpen(eventRecord)}><FiCheckCircle /> Open</button>
              {eventRecord.isAttending ? (
                <button type="button" onClick={() => onRemove(eventRecord)}><FiTrash2 /> Remove</button>
              ) : (
                <button type="button" onClick={() => onAttend(eventRecord)}><FiCheckCircle /> Attend</button>
              )}
              {isAdmin ? (
                <button type="button" onClick={() => onManage(eventRecord)}><FiEdit3 /> Manage</button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <button type="button" className="event-le-bento-main" onClick={() => onOpen(eventRecord)}>
        <span className="event-le-bento-image" style={{ backgroundImage: `url(${details.heroImageUrl || DEFAULT_BANNER_URL})` }} />
        <span className="event-le-bento-copy">
          <strong>{details.title}</strong>
          <span>{scheduleLabel || "Schedule pending"}</span>
          <small>{details.venue || "Event Location"}</small>
        </span>
      </button>

      <div className="event-le-bento-actions">
        {eventRecord.isAttending ? (
          <button
            type="button"
            className="event-le-secondary-action event-le-inline-action"
            onClick={() => onRemove(eventRecord)}
            disabled={isProcessing}
          >
            <FiCheckCircle /> {isProcessing ? "Saving..." : "Going"}
          </button>
        ) : (
          <button
            type="button"
            className="event-le-primary-action event-le-inline-action"
            onClick={() => onAttend(eventRecord)}
            disabled={isProcessing}
          >
            {isProcessing ? "Saving..." : "Attend"}
          </button>
        )}

        <EventCalendarActions details={details} eventId={eventId} compact />
      </div>

      <div className="event-le-bento-meta">
        <span>{eventRecord.attendeeCount || 0} going</span>
        {typeof eventRecord.distanceMiles === "number" ? <span>{eventRecord.distanceMiles.toFixed(1)} mi</span> : null}
        {onSelectLocation ? (
          <button type="button" onClick={() => onSelectLocation(eventId)}><FiMapPin /> Venue</button>
        ) : null}
        <span>{eventId}</span>
      </div>
    </article>
  );
};

const EventLeMapPanel = ({ events, venues, userLocation, selectedEventId, onSelectEvent, onOpenEvent, onOpenClub }) => {
  const [mapSearchQuery, setMapSearchQuery] = useState("");

  const normalizedSearchQuery = mapSearchQuery.trim().toLowerCase();
  const searchedEvents = normalizedSearchQuery
    ? events.filter((eventRecord) => {
      const details = mapLeEventToDetails(eventRecord);
      return [details.title, details.venue, details.venueAddress, details.venueLocation, details.venueCategory]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearchQuery));
    })
    : events;

  const mapEvents = searchedEvents
    .map((eventRecord, index) => {
      const eventId = normalizeEventId(eventRecord.eventId);
      const coordinates = getLeMapCoordinates(eventRecord, venues, userLocation, index);
      const venue = getClosestVenue(eventRecord, venues, userLocation);
      return { eventId, eventRecord, coordinates, venue };
    })
    .filter((entry) => entry.coordinates);
  const selectedMapEvent = mapEvents.find((entry) => entry.eventId === selectedEventId) || mapEvents[0];
  const selectedDetails = selectedMapEvent ? mapLeEventToDetails(selectedMapEvent.eventRecord) : null;
  const mapCenter = selectedMapEvent
    ? { lat: selectedMapEvent.coordinates.latitude, lng: selectedMapEvent.coordinates.longitude }
    : userLocation
      ? { lat: userLocation.latitude, lng: userLocation.longitude }
      : LE_MAP_DEFAULT_CENTER;
  const selectedVenueAddress = selectedDetails?.venueAddress || getVenueAddressText(selectedMapEvent?.venue);
  const selectedVenueDetails = selectedDetails ? {
    ...selectedDetails,
    venue: selectedDetails.venue || selectedMapEvent?.venue?.name || selectedMapEvent?.venue?.shopName || "Event Location",
    venueAddress: selectedVenueAddress,
    venueRating: getFirstValue(selectedDetails.venueRating, selectedMapEvent?.venue?.averageRating, null),
    venueReviewCount: getFirstValue(selectedDetails.venueReviewCount, selectedMapEvent?.venue?.reviewCount, selectedMapEvent?.venue?.reviewsCount, null),
    venueWebsite: getFirstValue(selectedDetails.venueWebsite, selectedMapEvent?.venue?.website, selectedMapEvent?.venue?.websiteUrl, ""),
    venuePhone: getFirstValue(selectedDetails.venuePhone, selectedMapEvent?.venue?.phoneNumber, ""),
    venueLatitude: selectedMapEvent.coordinates?.latitude ?? selectedDetails.venueLatitude,
    venueLongitude: selectedMapEvent.coordinates?.longitude ?? selectedDetails.venueLongitude,
  } : null;
  const mapPanelDetails = selectedVenueDetails || {
    venue: userLocation ? "Your Location" : "Event locations",
    venueLatitude: mapCenter.lat,
    venueLongitude: mapCenter.lng,
  };
  const directionsUrl = selectedVenueDetails ? buildGoogleMapsDirectionsUrl(selectedVenueDetails) : "";

  const handleSelect = (eventId) => {
    onSelectEvent(eventId);
  };

  return (
    <div className="event-le-map-panel">
      <div className="event-le-map-header">
        <div>
          <span><FiNavigation /> Map search</span>
          <strong>{userLocation ? "Events closest to you" : "Event locations"}</strong>
        </div>
        <small>{mapEvents.length} mapped</small>
      </div>

      <label className="event-le-map-search-field">
        <FiSearch />
        <input
          value={mapSearchQuery}
          onChange={(event) => setMapSearchQuery(event.target.value)}
          placeholder="Search events, venues, or city"
        />
      </label>

      <div className="event-le-map-canvas">
        <EventMapEmbed details={mapPanelDetails} title="GameBird map view" zoom={selectedMapEvent ? 15 : 10} />
      </div>

      {selectedMapEvent && selectedDetails ? (
        <div className="event-le-map-selected-card">
          <button type="button" className="event-le-map-selected-main" onClick={() => onOpenEvent(selectedMapEvent.eventRecord)}>
            <span className="event-le-bento-image" style={{ backgroundImage: `url(${selectedDetails.heroImageUrl || DEFAULT_BANNER_URL})` }} />
            <span>
              <small>{typeof selectedMapEvent.eventRecord.distanceMiles === "number" ? `${selectedMapEvent.eventRecord.distanceMiles.toFixed(1)} mi away` : selectedMapEvent.coordinates.source === "event" ? "Event location" : "Venue area"}</small>
              <strong>{selectedDetails.title}</strong>
              <em>{selectedDetails.venue || selectedVenueAddress || "Event Location"}</em>
            </span>
          </button>
          <div className="event-le-map-selected-actions">
            <button type="button" className="event-le-primary-action" onClick={() => onOpenEvent(selectedMapEvent.eventRecord)}>Open Event</button>
            <button type="button" className="event-le-secondary-action" onClick={() => onOpenClub(selectedMapEvent.eventRecord)}><FiInfo /> Club Details</button>
            {directionsUrl ? <a className="event-le-secondary-action" href={directionsUrl} target="_blank" rel="noreferrer"><FiNavigation /> Directions</a> : null}
          </div>
        </div>
      ) : null}

      {selectedVenueDetails ? (
        <div className="event-le-place-card">
          {selectedMapEvent.venue?.banner?.url ? <img src={selectedMapEvent.venue.banner.url} alt="" /> : null}
          <EventVenueInfoCard
            details={selectedVenueDetails}
            title="Place Info"
            actions={selectedMapEvent ? (
              <>
                <button type="button" className="event-le-secondary-action" onClick={() => onOpenClub(selectedMapEvent.eventRecord)}><FiInfo /> Club Details</button>
                {directionsUrl ? <a className="event-le-primary-action" href={directionsUrl} target="_blank" rel="noreferrer"><FiNavigation /> Directions</a> : null}
              </>
            ) : null}
          />
        </div>
      ) : null}

      <div className="event-le-map-event-strip" aria-label="Map event cards">
        {searchedEvents.map((eventRecord) => {
          const eventId = normalizeEventId(eventRecord.eventId);
          const details = mapLeEventToDetails(eventRecord);
          const isSelected = selectedMapEvent?.eventId === eventId;

          return (
            <button
              key={eventId}
              type="button"
              className={isSelected ? "selected" : ""}
              onClick={() => handleSelect(eventId)}
              onFocus={() => handleSelect(eventId)}
            >
              <strong>{details.title}</strong>
              <span>{typeof eventRecord.distanceMiles === "number" ? `${eventRecord.distanceMiles.toFixed(1)} mi` : details.venue || "Event Location"}</span>
            </button>
          );
        })}
        {!searchedEvents.length ? <div className="event-le-map-empty">No events match that search.</div> : null}
      </div>
    </div>
  );
};

const EventHubPage = ({ currentEventId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user: reduxUser } = useSelector((state) => state.user);
  const [activeMainTab, setActiveMainTab] = useState(() => (["events", "maps", "about"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "events"));
  const [eventScopeTab, setEventScopeTab] = useState("all");
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingEventId, setProcessingEventId] = useState("");
  const [openMenuEventId, setOpenMenuEventId] = useState("");
  const [showAdminManager, setShowAdminManager] = useState(false);
  const [adminEditEventId, setAdminEditEventId] = useState("");
  const [userLocation, setUserLocation] = useState(() => getStoredUserLocation());
  const [geoStatus, setGeoStatus] = useState(() => getStoredUserLocation() ? "granted" : "idle");
  const [geoMessage, setGeoMessage] = useState("");
  const [showGeoPrompt, setShowGeoPrompt] = useState(false);
  const [nearbyVenues, setNearbyVenues] = useState([]);
  const [yourEventTab, setYourEventTab] = useState("going");
  const [pastEventFilter, setPastEventFilter] = useState("all");
  const [nearbyRadius, setNearbyRadius] = useState("25");
  const [showNearbyMap, setShowNearbyMap] = useState(false);
  const [selectedNearbyEventId, setSelectedNearbyEventId] = useState("");
  const isAdmin = reduxUser?.role === "Admin";

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${server}/user/event/events`, { withCredentials: true });
      setEvents(data.events || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load events.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) {
      return undefined;
    }

    let permissionStatus;
    let isMounted = true;

    navigator.permissions.query({ name: "geolocation" }).then((status) => {
      if (!isMounted) {
        return;
      }

      permissionStatus = status;
      const syncPermissionState = () => {
        if (status.state === "prompt") {
          setGeoStatus((currentStatus) => currentStatus === "denied" ? userLocation ? "granted" : "idle" : currentStatus);
          setGeoMessage((currentMessage) => currentMessage.includes("blocked") ? "" : currentMessage);
        }

        if (status.state === "denied" && !userLocation) {
          setGeoStatus("idle");
          setGeoMessage("Location was not granted by the browser. Allow location for this site, then tap Use Location again.");
        }
      };

      status.onchange = syncPermissionState;
      syncPermissionState();
    }).catch(() => {});

    return () => {
      isMounted = false;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [userLocation]);

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (["events", "maps", "about"].includes(requestedTab)) {
      setActiveMainTab(requestedTab);
      if (requestedTab === "maps") {
        setShowNearbyMap(true);
        if (typeof window !== "undefined") {
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      }
    }

    const requestedEventId = searchParams.get("eventId");
    if (requestedEventId) {
      setSelectedNearbyEventId(normalizeEventId(requestedEventId));
    }
  }, [searchParams]);

  useEffect(() => {
    if (eventScopeTab !== "nearby" && activeMainTab !== "maps") {
      return undefined;
    }

    let isMounted = true;

    const loadNearbyVenues = async () => {
      try {
        const { data } = await axios.get(`${server}/shop/all-sellers`, { withCredentials: true });
        if (isMounted) {
          setNearbyVenues(data.sellers || []);
        }
      } catch (error) {
        if (isMounted) {
          setNearbyVenues([]);
        }
      }
    };

    loadNearbyVenues();

    return () => {
      isMounted = false;
    };
  }, [activeMainTab, eventScopeTab]);

  const handleOpenEvent = (eventRecord) => {
    navigate(`/event/${encodeURIComponent(normalizeEventId(eventRecord.eventId))}`);
  };

  const handleOpenClub = (eventRecord) => {
    navigate(`/event/${encodeURIComponent(normalizeEventId(eventRecord.eventId))}/club`);
  };

  const handleManageEvent = (eventRecord) => {
    setActiveMainTab("events");
    setEventScopeTab("create");
    setShowAdminManager(true);
    setAdminEditEventId(normalizeEventId(eventRecord.eventId));
    setOpenMenuEventId("");
  };

  const handleCreateEvent = () => {
    setActiveMainTab("events");
    setEventScopeTab("create");
    setAdminEditEventId("");
    setShowAdminManager(true);
  };

  const handleSelectAdminEvent = async (nextEventId) => {
    await loadEvents();
    setShowAdminManager(false);
    setAdminEditEventId("");
    setEventScopeTab("all");
    setActiveMainTab("events");
    if (nextEventId) {
      storeEventId(nextEventId);
    }
  };

  const handleOpenLeMapSearch = () => {
    setActiveMainTab("maps");
    setShowNearbyMap(true);
  };

  const requestUserLocation = () => {
    setShowGeoPrompt(false);

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoStatus("unavailable");
      setGeoMessage("Location is not available in this browser.");
      return;
    }

    setGeoStatus("loading");
    setGeoMessage("");

    const handleLocationSuccess = (position) => {
      const nextLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      storeUserLocation(nextLocation);
      setUserLocation(nextLocation);
      setGeoStatus("granted");
      setGeoMessage("Location active.");
      setShowNearbyMap(true);
    };

    const handleLocationFailure = (error, hasRetried = false) => {
      if (!hasRetried && error?.code !== 1) {
        navigator.geolocation.getCurrentPosition(
          handleLocationSuccess,
          (retryError) => handleLocationFailure(retryError, true),
          {
            enableHighAccuracy: false,
            timeout: 12000,
            maximumAge: 600000,
          }
        );
        return;
      }

      clearStoredUserLocation();
      setUserLocation(null);
      setGeoStatus("idle");
      setGeoMessage(getGeolocationErrorMessage(error));
    };

    navigator.geolocation.getCurrentPosition(
      handleLocationSuccess,
      (error) => handleLocationFailure(error),
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 300000,
      }
    );
  };

  const handleAttendEvent = async (eventRecord) => {
    if (!isAuthenticated) {
      navigate(eventPath("/event/login", eventRecord.eventId));
      return;
    }

    const eventId = normalizeEventId(eventRecord.eventId);
    setProcessingEventId(eventId);

    try {
      const { data } = await axios.post(`${server}/user/event/${encodeURIComponent(eventId)}/attendance`, {}, { withCredentials: true });
      setEvents((currentEvents) => mergeEventRecord(currentEvents, data.event));
      toast.success("You're going.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to attend this event.");
    } finally {
      setProcessingEventId("");
    }
  };

  const handleRemoveEvent = async (eventRecord) => {
    if (!isAuthenticated) {
      navigate(eventPath("/event/login", eventRecord.eventId));
      return;
    }

    const eventId = normalizeEventId(eventRecord.eventId);
    setProcessingEventId(eventId);
    setOpenMenuEventId("");

    try {
      const { data } = await axios.delete(`${server}/user/event/${encodeURIComponent(eventId)}/attendance`, { withCredentials: true });
      setEvents((currentEvents) => mergeEventRecord(currentEvents, data.event));
      toast.success("Removed from your events.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update this event.");
    } finally {
      setProcessingEventId("");
    }
  };

  const yourEvents = events.filter((eventRecord) => eventRecord.isAttending);
  const goingEvents = yourEvents.filter((eventRecord) => !isPastLeEvent(eventRecord))
    .sort((firstEvent, secondEvent) => (getEventTimeValue(firstEvent) || Number.POSITIVE_INFINITY) - (getEventTimeValue(secondEvent) || Number.POSITIVE_INFINITY));
  const currentEventList = yourEvents.filter((eventRecord) => normalizeEventId(eventRecord.eventId) === normalizeEventId(currentEventId));
  const pastEvents = filterPastEvents(
    yourEvents
      .filter((eventRecord) => isPastLeEvent(eventRecord))
      .sort((firstEvent, secondEvent) => (getEventTimeValue(secondEvent) || 0) - (getEventTimeValue(firstEvent) || 0)),
    pastEventFilter
  );
  const nearbyEvents = events
    .map((eventRecord) => {
      const eventCoordinates = getEventCoordinates(eventRecord);
      return {
        ...eventRecord,
        distanceMiles: calculateDistanceMiles(userLocation, eventCoordinates),
      };
    })
    .sort((firstEvent, secondEvent) => {
      const firstDistance = typeof firstEvent.distanceMiles === "number" ? firstEvent.distanceMiles : Number.POSITIVE_INFINITY;
      const secondDistance = typeof secondEvent.distanceMiles === "number" ? secondEvent.distanceMiles : Number.POSITIVE_INFINITY;
      return firstDistance - secondDistance;
    });
  const selectedRadius = NEARBY_RADIUS_OPTIONS.find((option) => option.value === nearbyRadius) || NEARBY_RADIUS_OPTIONS[1];
  const nearbyEventsInRadius = nearbyEvents.filter((eventRecord) => (
    selectedRadius.miles === Number.POSITIVE_INFINITY
      || typeof eventRecord.distanceMiles !== "number"
      || eventRecord.distanceMiles <= selectedRadius.miles
  ));
  const selectedNearbyEvent = selectedNearbyEventId
    ? nearbyEvents.find((eventRecord) => normalizeEventId(eventRecord.eventId) === normalizeEventId(selectedNearbyEventId))
    : null;
  const isFocusedMapView = Boolean(activeMainTab === "maps" && searchParams.get("eventId") && selectedNearbyEvent);
  const mapEventsForPanel = isFocusedMapView
    ? [selectedNearbyEvent]
    : selectedNearbyEvent && !nearbyEventsInRadius.some((eventRecord) => normalizeEventId(eventRecord.eventId) === normalizeEventId(selectedNearbyEventId))
    ? [selectedNearbyEvent, ...nearbyEventsInRadius]
    : nearbyEventsInRadius;
  const visibleEvents = eventScopeTab === "mine"
    ? yourEventTab === "past" ? pastEvents : yourEventTab === "current" ? currentEventList : goingEvents
    : eventScopeTab === "nearby" ? nearbyEventsInRadius
      : eventScopeTab === "create" ? []
        : events;
  const handlePrimaryTabChange = (tab) => {
    setActiveMainTab(tab);
    if (tab === "maps") {
      setShowNearbyMap(true);
    }
  };

  const eventProcessTitle = eventScopeTab === "create"
    ? "Create Event"
    : eventScopeTab === "mine"
      ? yourEventTab === "past" ? "Past Events" : yourEventTab === "current" ? "Current Event" : "Going"
      : eventScopeTab === "nearby" ? "Nearby Events" : "All Events";
  const eventProcessText = eventScopeTab === "create"
    ? "Add or update an event, then return to the event lists."
    : eventScopeTab === "nearby"
      ? `Showing events within ${selectedRadius.label}.`
      : "Browse event cards and open the compact event detail view.";

  return (
    <EventShell eyebrow="GameBird" title="Events" subtitle="Choose where you are going next.">
      <section className="event-le-card event-le-directory-card">
        <EventTopTabs activeTab={activeMainTab} eventId={currentEventId} localTabs={["events", "maps", "about"]} onLocalTab={handlePrimaryTabChange} />

        {activeMainTab === "about" ? (
          <div className="event-le-tab-panel">
            <EventProcessTitle icon={<FiCheckCircle />} eyebrow="About" title="GameBird event access" text="Compact passes, RSVPs, maps, and event details stay inside this app." />
            <div className="event-le-about-grid">
              <div className="event-le-about-tile">
                <span>Current event</span>
                <strong>{currentEventId}</strong>
              </div>
              <div className="event-le-about-tile">
                <span>Events live</span>
                <strong>{events.length}</strong>
              </div>
              <div className="event-le-about-tile">
                <span>Your RSVPs</span>
                <strong>{yourEvents.length}</strong>
              </div>
              <div className="event-le-about-copy">
                <h2>GameBird</h2>
                <p>Limited-entry access, event passes, RSVP status, maps, and place details are presented as one compact event app.</p>
              </div>
            </div>
          </div>
        ) : null}

        {activeMainTab === "events" ? (
          <>
            <div className="event-le-tabs event-le-secondary-tabs" role="tablist" aria-label="Event filters">
              {[
                ["all", "All Events"],
                ["mine", "Your Events"],
                ["nearby", "Near You"],
                ...(isAdmin ? [["create", "Create Event"]] : []),
              ].map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  className={eventScopeTab === tab ? "active" : ""}
                  onClick={() => {
                    if (tab === "create") {
                      handleCreateEvent();
                      return;
                    }
                    setEventScopeTab(tab);
                    setShowAdminManager(false);
                    setAdminEditEventId("");
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {eventScopeTab === "mine" ? (
              <div className="event-le-tabs event-le-tertiary-tabs" role="tablist" aria-label="Your event filters">
                {[
                  ["going", "Going"],
                  ["current", "Current Event"],
                  ["past", "Past Events"],
                ].map(([tab, label]) => (
                  <button
                    key={tab}
                    type="button"
                    className={yourEventTab === tab ? "active" : ""}
                    onClick={() => setYourEventTab(tab)}
                  >
                    {label}
                  </button>
                ))}
                {yourEventTab === "past" ? (
                  <label className="event-le-filter-select">
                    Filter by
                    <select value={pastEventFilter} onChange={(event) => setPastEventFilter(event.target.value)}>
                      <option value="all">All past</option>
                      <option value="30">Last 30 days</option>
                      <option value="year">This year</option>
                    </select>
                  </label>
                ) : null}
              </div>
            ) : null}

            <div className="event-le-tab-panel event-le-event-process-panel">
              <EventProcessTitle icon={eventScopeTab === "nearby" ? <FiNavigation /> : eventScopeTab === "create" ? <FiPlus /> : <FiCheckCircle />} eyebrow="Events" title={eventProcessTitle} text={eventProcessText} />
            </div>

            {eventScopeTab === "create" && isAdmin && showAdminManager ? (
              <div className="event-le-admin-drawer">
                <EventAdminManager
                  currentEventId={currentEventId}
                  initialEditEventId={adminEditEventId}
                  onSelectEvent={handleSelectAdminEvent}
                  onEventsChanged={loadEvents}
                  showEventList={false}
                />
              </div>
            ) : null}

            {eventScopeTab === "nearby" ? (
              <div className="event-le-nearby-panel">
                <div className="event-le-geo-card">
                  <div>
                    <span><FiNavigation /> {geoStatus === "granted" ? "Location active" : "Location needed"}</span>
                    <strong>{geoStatus === "granted" ? "Find events near you" : "Find nearby events"}</strong>
                    {geoMessage ? <p>{geoMessage}</p> : <p>Allow location access to sort event cards by what is closest to you.</p>}
                  </div>
                  <button type="button" className="event-le-small-button" onClick={requestUserLocation} disabled={geoStatus === "loading"}>
                    <FiSearch /> {geoStatus === "loading" ? "Locating" : geoStatus === "granted" ? "Refresh" : "Use Location"}
                  </button>
                </div>

                <div className="event-le-radius-card">
                  <span><FiMapPin /> Distance Radius</span>
                  <div className="event-le-radius-options" role="tablist" aria-label="Nearby distance radius">
                    {NEARBY_RADIUS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={nearbyRadius === option.value ? "active" : ""}
                        onClick={() => setNearbyRadius(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {showGeoPrompt ? (
                  <div className="event-le-geo-modal" role="dialog" aria-modal="true" aria-label="Location access">
                    <div className="event-le-geo-modal-card">
                      <button type="button" className="event-le-location-close" onClick={() => setShowGeoPrompt(false)} aria-label="Close location request">
                        <FiX />
                      </button>
                      <span><FiNavigation /> Location access</span>
                      <strong>Find events near you</strong>
                      <p>Allow location access to sort events and place cards by what is closest to you.</p>
                      <div className="event-le-attend-options">
                        <button type="button" className="event-le-primary-action" onClick={requestUserLocation}>Allow Location</button>
                        <button type="button" className="event-le-secondary-action" onClick={() => setShowGeoPrompt(false)}>Not Now</button>
                      </div>
                    </div>
                  </div>
                ) : null}

                <button type="button" className="event-le-secondary-action" onClick={handleOpenLeMapSearch}>
                  <FiMapPin /> Open Maps
                </button>
              </div>
            ) : null}

            {isLoading ? <div className="event-le-loading-strip">Loading events...</div> : null}

            {!isLoading && eventScopeTab === "mine" && !isAuthenticated ? (
              <div className="event-le-empty-state">
                <strong>Sign in to see your events.</strong>
                <div className="event-le-attend-options">
                  <Link className="event-le-secondary-action" to={eventPath("/event/login", currentEventId)}>Sign In</Link>
                  <Link className="event-le-secondary-action" to={eventPath("/event/signup", currentEventId)}>Create Account</Link>
                </div>
              </div>
            ) : null}

            {!isLoading && visibleEvents.length ? (
              <div className="event-le-bento-grid">
                {visibleEvents.map((eventRecord) => {
                  const eventId = normalizeEventId(eventRecord.eventId);
                  return (
                    <EventBentoCard
                      key={eventId}
                      eventRecord={eventRecord}
                      isAdmin={isAdmin}
                      isProcessing={processingEventId === eventId}
                      isMenuOpen={openMenuEventId === eventId}
                      showMenu
                      onAttend={handleAttendEvent}
                      onRemove={handleRemoveEvent}
                      onManage={handleManageEvent}
                      onOpen={handleOpenEvent}
                      onSelectLocation={eventScopeTab === "nearby" ? (nextEventId) => {
                        setSelectedNearbyEventId(nextEventId);
                        setActiveMainTab("maps");
                        setShowNearbyMap(true);
                      } : undefined}
                      onToggleMenu={setOpenMenuEventId}
                    />
                  );
                })}
              </div>
            ) : null}

            {!isLoading && eventScopeTab !== "create" && !visibleEvents.length && (eventScopeTab !== "mine" || isAuthenticated) ? (
              <div className="event-le-empty-state">
                <strong>{eventScopeTab === "mine" ? (yourEventTab === "past" ? "No past events yet." : yourEventTab === "current" ? "No current event yet." : "No events going yet.") : "No events available."}</strong>
                {eventScopeTab === "mine" ? <button type="button" className="event-le-secondary-action" onClick={() => setEventScopeTab("all")}>Browse Events</button> : null}
              </div>
            ) : null}
          </>
        ) : null}

        {activeMainTab === "maps" ? (
          <div className="event-le-tab-panel event-le-maps-process">
            <EventProcessTitle icon={<FiMapPin />} eyebrow="Maps" title="Search event locations" text="Find event cards, place details, and nearby experiences without leaving this app." />

            <div className="event-le-nearby-panel event-le-maps-panel">
              <div className="event-le-geo-card">
                <div>
                  <span><FiNavigation /> {geoStatus === "granted" ? "Location active" : "Location needed"}</span>
                  <strong>{geoStatus === "granted" ? "Map is centered around you" : "Use your location"}</strong>
                  {geoMessage ? <p>{geoMessage}</p> : <p>Allow location access to sort mapped event cards by distance.</p>}
                </div>
                <button type="button" className="event-le-small-button" onClick={requestUserLocation} disabled={geoStatus === "loading"}>
                  <FiSearch /> {geoStatus === "loading" ? "Locating" : geoStatus === "granted" ? "Refresh" : "Use Location"}
                </button>
              </div>

              <div className="event-le-radius-card">
                <span><FiMapPin /> Distance Radius</span>
                <div className="event-le-radius-options" role="tablist" aria-label="Map distance radius">
                  {NEARBY_RADIUS_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={nearbyRadius === option.value ? "active" : ""}
                      onClick={() => setNearbyRadius(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {showGeoPrompt ? (
                <div className="event-le-geo-modal" role="dialog" aria-modal="true" aria-label="Location access">
                  <div className="event-le-geo-modal-card">
                    <button type="button" className="event-le-location-close" onClick={() => setShowGeoPrompt(false)} aria-label="Close location request">
                      <FiX />
                    </button>
                    <span><FiNavigation /> Location access</span>
                    <strong>Find events near you</strong>
                    <p>Allow location access to sort event and place cards by what is closest to you.</p>
                    <div className="event-le-attend-options">
                      <button type="button" className="event-le-primary-action" onClick={requestUserLocation}>Allow Location</button>
                      <button type="button" className="event-le-secondary-action" onClick={() => setShowGeoPrompt(false)}>Not Now</button>
                    </div>
                  </div>
                </div>
              ) : null}

              <EventLeMapPanel
                events={mapEventsForPanel}
                venues={nearbyVenues}
                userLocation={userLocation}
                selectedEventId={selectedNearbyEventId}
                onSelectEvent={setSelectedNearbyEventId}
                onOpenEvent={handleOpenEvent}
                onOpenClub={handleOpenClub}
              />
            </div>
          </div>
        ) : null}
      </section>
    </EventShell>
  );
};

const EventDetailPage = () => {
  const eventId = useEventId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useSelector((state) => state.user);
  const [showAttendOptions, setShowAttendOptions] = useState(false);
  const fallbackDetails = useMemo(() => getEventDetails(searchParams), [searchParams]);
  const [eventDetails, setEventDetails] = useState(() => mapLeEventToDetails(null, fallbackDetails));
  const [eventPassIdentity, setEventPassIdentity] = useState(null);
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [isAttendanceSaving, setIsAttendanceSaving] = useState(false);

  const loadEventPassIdentity = useCallback(async () => {
    if (!isAuthenticated) {
      setEventPassIdentity(null);
      return;
    }

    try {
      const { data } = await axios.get(`${server}/user/event/pass`, {
        withCredentials: true,
        params: { eventId },
      });
      setEventPassIdentity(data);
    } catch (error) {
      setEventPassIdentity(null);
    }
  }, [eventId, isAuthenticated]);

  useEffect(() => {
    let isMounted = true;

    const loadEventDetails = async () => {
      setIsEventLoading(true);
      try {
        const { data } = await axios.get(`${server}/user/event/${encodeURIComponent(eventId)}`, { withCredentials: true });
        if (isMounted) {
          setEventDetails(mapLeEventToDetails(data.event, fallbackDetails));
        }
      } catch (error) {
        if (isMounted) {
          setEventDetails(mapLeEventToDetails(null, fallbackDetails));
        }
      } finally {
        if (isMounted) {
          setIsEventLoading(false);
        }
      }
    };

    loadEventDetails();

    return () => {
      isMounted = false;
    };
  }, [eventId, fallbackDetails]);

  useEffect(() => {
    loadEventPassIdentity();
  }, [loadEventPassIdentity]);

  const handleAttendEvent = async () => {
    if (!isAuthenticated) {
      setShowAttendOptions(true);
      return;
    }

    setIsAttendanceSaving(true);
    try {
      const { data } = await axios.post(`${server}/user/event/${encodeURIComponent(eventId)}/attendance`, {}, { withCredentials: true });
      setEventDetails(mapLeEventToDetails(data.event, fallbackDetails));
      await loadEventPassIdentity();
      toast.success("You're going.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to attend this event.");
    } finally {
      setIsAttendanceSaving(false);
    }
  };

  const handleRemoveEvent = async () => {
    setIsAttendanceSaving(true);
    try {
      const { data } = await axios.delete(`${server}/user/event/${encodeURIComponent(eventId)}/attendance`, { withCredentials: true });
      setEventDetails(mapLeEventToDetails(data.event, fallbackDetails));
      setEventPassIdentity(null);
      toast.success("Removed from your events.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update this event.");
    } finally {
      setIsAttendanceSaving(false);
    }
  };

  const eventQrCodeUrl = eventDetails.isAttending
    ? eventPassIdentity?.scan?.eventQrCodeUrl || eventDetails.eventQrCodeUrl
    : eventDetails.eventQrCodeUrl;
  const eventQrLabel = eventDetails.isAttending ? "Scan In" : "Event QR";
  const eventQrHandle = eventPassIdentity?.attendee?.handle || "event";
  const eventScheduleLabel = getEventScheduleLabel(eventDetails);
  const handleOpenEventMaps = () => navigate(`/event?tab=maps&eventId=${encodeURIComponent(eventId)}`);
  const handleOpenClubDetails = () => navigate(`/event/${encodeURIComponent(eventId)}/club`);

  return (
    <EventShell
      eyebrow="GameBird Event"
      title={eventDetails.title}
      subtitle={eventDetails.summary}
    >
      <section className="event-le-card event-le-directory-card event-le-detail-nav-card">
        <EventTopTabs activeTab="events" eventId={eventId} />
      </section>

      <section className="event-le-card event-le-event-card">
        <div className="event-le-event-hero" style={{ backgroundImage: `linear-gradient(135deg, rgba(31, 91, 53, 0.94), rgba(23, 32, 23, 0.88)), url(${eventDetails.heroImageUrl || DEFAULT_BANNER_URL})` }}>
          <div className="event-le-entry-icon"><FiCheckCircle /></div>
        </div>

        {isEventLoading ? <div className="event-le-loading-strip">Loading event...</div> : null}

        <div className="event-le-detail-title-block">
          <h2>{eventDetails.title}</h2>
          <p>{eventDetails.summary}</p>
        </div>

        <div className="event-le-event-details">
          <div>
            <span>Venue</span>
            <strong>{eventDetails.venue}</strong>
          </div>
          {eventDetails.venueAddress ? (
            <div>
              <span>Address</span>
              <strong>{eventDetails.venueAddress}</strong>
            </div>
          ) : null}
          <div>
            <span>Schedule</span>
            <strong>{eventScheduleLabel}</strong>
          </div>
        </div>

        <EventDetailVenueMap details={eventDetails} eventId={eventId} onOpenMaps={handleOpenEventMaps} onOpenClub={handleOpenClubDetails} />

        <p className="event-le-event-id">Event ID: {eventId}</p>

        {eventQrCodeUrl ? (
          <div className="event-le-event-qr-card">
            <img src={eventQrCodeUrl} alt={`${eventDetails.title} QR code`} />
            <div>
              <span>{eventQrLabel}</span>
              <strong>{eventDetails.title}</strong>
              <p>{eventDetails.isAttending ? `@${eventQrHandle}` : eventId}</p>
            </div>
          </div>
        ) : null}

        <div className="event-le-action-stack">
          {isAuthenticated ? (
            eventDetails.isAttending ? (
              <>
                <Link className="event-le-primary-action" to={eventPath("/event/pass", eventId)}>
                  <FiCheckCircle /> Going
                </Link>
                <button type="button" className="event-le-secondary-action" onClick={handleRemoveEvent} disabled={isAttendanceSaving}>
                  {isAttendanceSaving ? "Saving..." : "Remove from Your Events"}
                </button>
              </>
            ) : (
              <button type="button" className="event-le-primary-action" onClick={handleAttendEvent} disabled={isAttendanceSaving}>
                {isAttendanceSaving ? "Saving..." : "Attend"}
              </button>
            )
          ) : (
            <>
              <button
                type="button"
                className="event-le-primary-action"
                onClick={handleAttendEvent}
              >
                Attend
              </button>
              {showAttendOptions ? (
                <div className="event-le-attend-options">
                  <Link className="event-le-secondary-action" to={eventPath("/event/login", eventId)}>
                    Sign In
                  </Link>
                  <Link className="event-le-secondary-action" to={eventPath("/event/signup", eventId)}>
                    Create Account
                  </Link>
                </div>
              ) : null}
            </>
          )}

          <EventCalendarActions details={eventDetails} eventId={eventId} />

          <button type="button" className="event-le-secondary-action" onClick={() => navigate(eventPath("/event", eventId))}>
            <FiArrowLeft /> Back to Events
          </button>
        </div>
      </section>
    </EventShell>
  );
};

const EventClubPage = () => {
  const eventId = useEventId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fallbackDetails = useMemo(() => getEventDetails(searchParams), [searchParams]);
  const [eventDetails, setEventDetails] = useState(() => mapLeEventToDetails(null, fallbackDetails));
  const [isEventLoading, setIsEventLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadEventDetails = async () => {
      setIsEventLoading(true);
      try {
        const { data } = await axios.get(`${server}/user/event/${encodeURIComponent(eventId)}`, { withCredentials: true });
        if (isMounted) {
          setEventDetails(mapLeEventToDetails(data.event, fallbackDetails));
        }
      } catch (error) {
        if (isMounted) {
          setEventDetails(mapLeEventToDetails(null, fallbackDetails));
        }
      } finally {
        if (isMounted) {
          setIsEventLoading(false);
        }
      }
    };

    loadEventDetails();

    return () => {
      isMounted = false;
    };
  }, [eventId, fallbackDetails]);

  const eventScheduleLabel = getEventScheduleLabel(eventDetails);
  const directionsUrl = buildGoogleMapsDirectionsUrl(eventDetails);
  const handleBackToEvent = () => navigate(`/event/${encodeURIComponent(eventId)}`);
  const handleViewMap = () => navigate(`/event?tab=maps&eventId=${encodeURIComponent(eventId)}`);

  return (
    <EventShell
      eyebrow="Club Details"
      title={eventDetails.venue || "Event Location"}
      subtitle={eventDetails.venueAddress || eventDetails.venueLocation || eventDetails.title}
    >
      <section className="event-le-card event-le-directory-card event-le-detail-nav-card">
        <EventTopTabs activeTab="events" eventId={eventId} />
      </section>

      <section className="event-le-card event-le-club-page-card">
        {isEventLoading ? <div className="event-le-loading-strip">Loading club details...</div> : null}

        <div className="event-le-club-heading">
          <button type="button" className="event-le-secondary-action" onClick={handleBackToEvent}>
            <FiArrowLeft /> Back to Event
          </button>
          <div>
            <span><FiMapPin /> Club Profile</span>
            <h2>{eventDetails.venue || "Event Location"}</h2>
            <p>{eventDetails.venueAddress || eventDetails.venueLocation || "Location details pending."}</p>
          </div>
        </div>

        <div className="event-le-club-map-block">
          <EventMapEmbed details={eventDetails} title={`${eventDetails.venue || eventId} club map`} zoom={15} />
        </div>

        <div className="event-le-event-details event-le-club-event-details">
          <div>
            <span>Event</span>
            <strong>{eventDetails.title}</strong>
          </div>
          <div>
            <span>Schedule</span>
            <strong>{eventScheduleLabel}</strong>
          </div>
          {eventDetails.venueCategory ? (
            <div>
              <span>Club Type</span>
              <strong>{eventDetails.venueCategory}</strong>
            </div>
          ) : null}
          {formatVenueRating(eventDetails.venueRating, eventDetails.venueReviewCount) ? (
            <div>
              <span>Rating</span>
              <strong>{formatVenueRating(eventDetails.venueRating, eventDetails.venueReviewCount)}</strong>
            </div>
          ) : null}
        </div>

        <EventVenueInfoCard
          details={eventDetails}
          title="Club Details"
          actions={(
            <>
              {directionsUrl ? (
                <a className="event-le-primary-action" href={directionsUrl} target="_blank" rel="noreferrer">
                  <FiNavigation /> Directions
                </a>
              ) : null}
              <button type="button" className="event-le-secondary-action" onClick={handleViewMap}>
                <FiMapPin /> View Map
              </button>
              <button type="button" className="event-le-secondary-action" onClick={handleBackToEvent}>
                <FiArrowLeft /> Back to Event
              </button>
            </>
          )}
        />
      </section>
    </EventShell>
  );
};

const EventLandingPage = () => {
  const eventId = useEventId();
  const params = useParams();

  if (!params.eventId) {
    return <EventHubPage currentEventId={eventId} />;
  }

  return <EventDetailPage />;
};

const EventSignupPage = () => {
  const eventId = useEventId();
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [handleState, setHandleState] = useState({ checking: false, available: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const latestHandleRef = useRef("");

  useEffect(() => {
    const normalizedHandle = handle.toLowerCase().replace(/\s+/g, "");
    latestHandleRef.current = normalizedHandle;

    if (!normalizedHandle) {
      setHandleState({ checking: false, available: null, message: "" });
      return undefined;
    }

    if (!validateHandle(normalizedHandle)) {
      setHandleState({ checking: false, available: false, message: "Letters and numbers only." });
      return undefined;
    }

    setHandleState({ checking: true, available: null, message: "Checking handle..." });
    const timeoutId = window.setTimeout(async () => {
      try {
        const { data } = await axios.post(`${server}/user/check-handle`, { handle: normalizedHandle });

        if (latestHandleRef.current !== normalizedHandle) {
          return;
        }

        setHandle(data.handle || normalizedHandle);
        setHandleState({
          checking: false,
          available: Boolean(data.available),
          message: data.available ? "Handle available." : data.message || "Handle is already taken.",
        });
      } catch (error) {
        if (latestHandleRef.current !== normalizedHandle) {
          return;
        }

        setHandleState({
          checking: false,
          available: false,
          message: error.response?.data?.message || "Unable to check handle.",
        });
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [handle]);

  const handleSignup = async (event) => {
    event.preventDefault();

    if (!validateHandle(handle)) {
      setHandleState({ checking: false, available: false, message: "Letters and numbers only." });
      return;
    }

    if (handleState.available === false) {
      return;
    }

    if (!hasAcceptedTerms) {
      toast.error("Accept the Terms of Service to continue.");
      return;
    }

    const normalizedPhoneNumber = phoneNumber.replace(/\D/g, "");

    if (!normalizedPhoneNumber) {
      toast.error("Enter a phone number to continue.");
      return;
    }

    setIsSubmitting(true);
    setVerificationEmail("");

    try {
      const avatar = avatarFile
        ? await readImageFileAsDataUrl(await prepareImageFileForUpload(avatarFile))
        : undefined;
      const userBanner = bannerFile
        ? await readImageFileAsDataUrl(await prepareImageFileForUpload(bannerFile))
        : undefined;

      await axios.post(`${server}/user/create-user`, {
        name,
        handle,
        email,
        password,
        phoneNumber: normalizedPhoneNumber,
        activationPath: "/event/activation",
        eventId,
        avatar,
        userBanner,
      });
      setVerificationEmail(email.trim());
      toast.success("Verification email sent.");
      setName("");
      setHandle("");
      setEmail("");
      setPassword("");
      setPhoneNumber("");
      setAvatarFile(null);
      setBannerFile(null);
      setHasAcceptedTerms(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to create account.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EventShell title="Create Account" subtitle="Your GameBird event pass starts here.">
      <EventPrimaryNavCard activeTab="account" eventId={eventId} />
      <section className="event-le-card">
        {verificationEmail ? (
          <div className="event-le-notice" role="status">
            <FiCheckCircle />
            <div>
              <h2>Check Your Email</h2>
              <p>{verificationEmail}</p>
              <Link className="event-le-text-link" to={eventPath("/event/login", eventId)}>Go to sign in</Link>
            </div>
          </div>
        ) : null}

        {!verificationEmail ? (
          <>
            <form className="event-le-form" onSubmit={handleSignup}>
              <label>
                Full Name
                <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required />
              </label>

              <label>
                Handle
                <input
                  value={handle}
                  onChange={(event) => setHandle(event.target.value.toLowerCase().replace(/\s+/g, ""))}
                  autoComplete="username"
                  maxLength={30}
                  required
                />
                {handleState.message ? (
                  <span className={`event-le-field-note ${handleState.available === false ? "is-error" : ""}`}>
                    {handleState.message}
                  </span>
                ) : null}
              </label>

              <label>
                Email address
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required />
              </label>

              <label>
                Password
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required />
              </label>

              <label>
                Phone Number
                <input value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} autoComplete="tel" inputMode="tel" required />
              </label>

              <label className="event-le-file-field">
                Avatar
                <span className="event-le-file-button">{avatarFile ? avatarFile.name : "Upload a file"}</span>
                <input type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={(event) => setAvatarFile(event.target.files?.[0] || null)} />
              </label>

              <label className="event-le-file-field">
                Banner
                <span className="event-le-file-button">{bannerFile ? bannerFile.name : "Upload a Banner"}</span>
                <input type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={(event) => setBannerFile(event.target.files?.[0] || null)} />
              </label>

              <label className="event-le-checkbox-field">
                <input type="checkbox" checked={hasAcceptedTerms} onChange={(event) => setHasAcceptedTerms(event.target.checked)} required />
                <span>I agree to the Terms of Service</span>
              </label>

              <button className="event-le-primary-action" type="submit" disabled={isSubmitting || handleState.checking}>
                <FiUserPlus /> {isSubmitting ? "Creating..." : "Submit"}
              </button>
            </form>

            <div className="event-le-auth-links">
              <span>Already have an account?</span>
              <Link className="event-le-text-link" to={eventPath("/event/login", eventId)}>Sign In</Link>
            </div>
          </>
        ) : null}
      </section>
    </EventShell>
  );
};

const EventLoginPage = () => {
  const eventId = useEventId();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const { data } = await axios.post(`${server}/user/login-user`, { identifier, password }, { withCredentials: true });
      const token = data.token || data.user?.token;

      dispatch(setUser(data.user));
      cacheAuthUser(data.user);
      persistAuthToken(token);
      setStoredCurrentProfile("User");
      toast.success("Signed in.");
      navigate(eventPath("/event/pass", eventId), { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to sign in.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EventShell title="Sign In" subtitle="Open your GameBird event pass.">
      <EventPrimaryNavCard activeTab="account" eventId={eventId} />
      <section className="event-le-card">
        <form className="event-le-form" onSubmit={handleLogin}>
          <label>
            Email or Handle
            <input value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" required />
          </label>

          <label>
            Password
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
          </label>

          <button className="event-le-primary-action" type="submit" disabled={isSubmitting}>
            <FiLogIn /> {isSubmitting ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div className="event-le-auth-links">
          <Link className="event-le-text-link" to={eventPath("/event/signup", eventId)}>Create an account</Link>
        </div>
      </section>
    </EventShell>
  );
};

const EventActivationPage = () => {
  const eventId = useEventId();
  const { activation_token: activationToken } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Verifying account...");
  const [resendIdentifier, setResendIdentifier] = useState("");
  const activationAttemptedRef = useRef("");

  useEffect(() => {
    if (!activationToken || activationAttemptedRef.current === activationToken) {
      return;
    }

    activationAttemptedRef.current = activationToken;

    const activate = async () => {
      try {
        const { data } = await axios.post(`${server}/user/activation`, {
          activation_token: activationToken,
          activationPath: "/event/activation",
          eventId,
        }, { withCredentials: true });
        setMessage(data.message || "Account verified. Sign in to continue.");
        setStatus("success");
        window.setTimeout(() => navigate(eventPath("/event/login", eventId), { replace: true }), 900);
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.message || "Unable to verify this link.");
      }
    };

    activate();
  }, [activationToken, eventId, navigate]);

  const handleResend = async (event) => {
    event.preventDefault();

    if (!resendIdentifier.trim()) {
      setMessage("Enter your email, handle, or unique ID.");
      return;
    }

    try {
      const { data } = await axios.post(`${server}/user/activation/resend`, {
        identifier: resendIdentifier.trim(),
        activationPath: "/event/activation",
        eventId,
      });
      setMessage(data.message || "Verification email sent.");
    } catch (error) {
      setMessage(error.response?.data?.message || "Unable to resend verification.");
    }
  };

  return (
    <EventShell title="Email Verification" subtitle="Confirming your GameBird account.">
      <EventPrimaryNavCard activeTab="account" eventId={eventId} />
      <section className="event-le-card">
        <div className={`event-le-status event-le-status-${status}`}>
          {status === "loading" ? <FiRefreshCw /> : <FiCheckCircle />}
          <p>{message}</p>
        </div>

        {status === "error" ? (
          <form className="event-le-form" onSubmit={handleResend}>
            <label>
              Email, Handle, or Unique ID
              <input value={resendIdentifier} onChange={(event) => setResendIdentifier(event.target.value)} />
            </label>
            <button className="event-le-secondary-action" type="submit">Resend Verification</button>
          </form>
        ) : null}

        <Link className="event-le-text-link" to={eventPath("/event/login", eventId)}>Go to sign in</Link>
      </section>
    </EventShell>
  );
};

const EventCheckInPage = () => {
  const params = useParams();
  const eventId = normalizeEventId(params.eventId);
  const uniqueId = String(params.uniqueId || "").trim();
  const [status, setStatus] = useState("loading");
  const [scanResult, setScanResult] = useState(null);
  const [message, setMessage] = useState("Checking event pass...");

  useEffect(() => {
    let isMounted = true;

    const checkIn = async () => {
      if (!uniqueId) {
        setStatus("error");
        setMessage("Missing attendee ID.");
        return;
      }

      try {
        const { data } = await axios.get(
          `${server}/user/event/check-in/${encodeURIComponent(eventId)}/${encodeURIComponent(uniqueId)}`
        );

        if (!isMounted) {
          return;
        }

        setScanResult(data);
        setStatus("success");
        setMessage("Event pass verified.");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setStatus("error");
        setMessage(error.response?.data?.message || "Unable to verify this event pass.");
      }
    };

    checkIn();

    return () => {
      isMounted = false;
    };
  }, [eventId, uniqueId]);

  const attendee = scanResult?.attendee || {};

  return (
    <EventShell eyebrow="GameBird" title="Event Scan">
      <EventPrimaryNavCard activeTab="events" eventId={eventId} />
      <section className="event-le-card event-le-checkin-card">
        <div className={`event-le-status event-le-status-${status}`}>
          {status === "loading" ? <FiRefreshCw /> : <FiCheckCircle />}
          <p>{message}</p>
        </div>

        {status === "success" ? (
          <div className="event-le-checkin-details">
            <div><span>Name</span><strong>{attendee.name || "-"}</strong></div>
            <div><span>Handle</span><strong>@{attendee.handle || "-"}</strong></div>
            <div><span>Event</span><strong>{scanResult.eventId || eventId}</strong></div>
            <div><span>Unique ID</span><strong>{attendee.uniqueId || uniqueId}</strong></div>
          </div>
        ) : null}

        <Link className="event-le-secondary-action" to={`/event/${encodeURIComponent(eventId)}`}>Open Event</Link>
      </section>
    </EventShell>
  );
};

const getBlankAdminEventForm = (eventId = createAlphanumericEventId()) => ({
  eventId: normalizeEventId(eventId),
  title: "GameBird Live Event",
  venue: "Event Location",
  venueAddress: "",
  venueLocation: "",
  venueCategory: "",
  venueRating: "",
  venueReviewCount: "",
  venueHours: "",
  venueWebsite: "",
  venuePhone: "",
  venueLatitude: "",
  venueLongitude: "",
  schedule: "",
  summary: "Open your event pass, then scan in at the physical location to play.",
  startsDate: "",
  startsTime: "",
  endsDate: "",
  endsTime: "",
  heroImageUrl: "",
  displayOnLe: true,
  displayOnPrimary: false,
  status: "published",
});

const toLocalDateParts = (value) => {
  if (!value) {
    return { date: "", time: "" };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: "", time: "" };
  }

  const offsetMs = date.getTimezoneOffset() * 60000;
  const localDateTime = new Date(date.getTime() - offsetMs).toISOString();
  return {
    date: localDateTime.slice(0, 10),
    time: localDateTime.slice(11, 16),
  };
};

const combineLocalDateAndTime = (dateValue, timeValue, fallbackDate = "", fallbackToEndOfDay = false) => {
  const datePart = dateValue || fallbackDate;
  if (!datePart) {
    return "";
  }

  const timePart = timeValue || (fallbackToEndOfDay ? "23:59" : "00:00");
  const date = new Date(`${datePart}T${timePart}`);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const mapAdminEventToForm = (eventRecord) => {
  const startsAt = toLocalDateParts(eventRecord.startsAt);
  const endsAt = toLocalDateParts(eventRecord.endsAt);
  const details = mapLeEventToDetails(eventRecord);

  return {
    eventId: eventRecord.eventId || "",
    title: details.title || "",
    venue: details.venue || "",
    venueAddress: details.venueAddress || "",
    venueLocation: details.venueLocation || "",
    venueCategory: details.venueCategory || "",
    venueRating: details.venueRating === null || details.venueRating === undefined ? "" : String(details.venueRating),
    venueReviewCount: details.venueReviewCount === null || details.venueReviewCount === undefined ? "" : String(details.venueReviewCount),
    venueHours: details.venueHours || "",
    venueWebsite: details.venueWebsite || "",
    venuePhone: details.venuePhone || "",
    venueLatitude: details.venueLatitude === null || details.venueLatitude === undefined ? "" : String(details.venueLatitude),
    venueLongitude: details.venueLongitude === null || details.venueLongitude === undefined ? "" : String(details.venueLongitude),
    schedule: eventRecord.schedule === "Schedule pending" ? "" : eventRecord.schedule || "",
    summary: details.summary || "",
    startsDate: startsAt.date,
    startsTime: startsAt.time,
    endsDate: endsAt.date,
    endsTime: endsAt.time,
    heroImageUrl: eventRecord.heroImageUrl || "",
    displayOnLe: eventRecord.displayOnLe !== false,
    displayOnPrimary: eventRecord.displayOnPrimary === true,
    status: eventRecord.status || "published",
  };
};

const buildEventProfileEditForm = (attendee = {}) => ({
  name: attendee.name || "",
  email: attendee.email || "",
  handle: attendee.handle || "",
  phoneNumber: attendee.phoneNumber || "",
});

const buildPasswordEditForm = () => ({ oldPassword: "", newPassword: "", confirmPassword: "" });

const normalizeHandleInput = (value = "") => String(value).replace(/^@+/, "").replace(/[^a-z0-9]+/gi, "").toLowerCase();

const isFilledFormValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const validateAdminEventVenueForm = (form) => {
  const hasAddress = isFilledFormValue(form.venueAddress);
  const hasLatitude = isFilledFormValue(form.venueLatitude);
  const hasLongitude = isFilledFormValue(form.venueLongitude);

  if (!hasAddress && !(hasLatitude && hasLongitude)) {
    return "Add an event address or provide both venue latitude and venue longitude.";
  }

  if (hasLatitude !== hasLongitude) {
    return "Provide both venue latitude and venue longitude, or use the event address only.";
  }

  if (hasLatitude && hasLongitude) {
    const latitude = Number(form.venueLatitude);
    const longitude = Number(form.venueLongitude);

    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      return "Venue latitude must be a valid number between -90 and 90.";
    }

    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      return "Venue longitude must be a valid number between -180 and 180.";
    }
  }

  if (isFilledFormValue(form.venueRating)) {
    const rating = Number(form.venueRating);
    if (!Number.isFinite(rating) || rating < 0 || rating > 5) {
      return "Venue rating must be between 0 and 5.";
    }
  }

  return "";
};

const EventAdminManager = ({ currentEventId, initialEditEventId = "", onSelectEvent, onEventsChanged, showEventList = true }) => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(() => getBlankAdminEventForm());
  const [editingEventId, setEditingEventId] = useState("");
  const [openMenuEventId, setOpenMenuEventId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreparingHeroImage, setIsPreparingHeroImage] = useState(false);

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${server}/user/event/admin/events`, { withCredentials: true });
      setEvents(data.events || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load events.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleFormChange = (field, value) => {
    setForm((currentForm) => ({ ...currentForm, [field]: value }));
  };

  const resetForm = () => {
    setForm(getBlankAdminEventForm());
    setEditingEventId("");
    setOpenMenuEventId("");
  };

  const handleEdit = useCallback((eventRecord) => {
    setEditingEventId(eventRecord.eventId);
    setForm(mapAdminEventToForm(eventRecord));
    setOpenMenuEventId("");
  }, []);

  useEffect(() => {
    if (!initialEditEventId || !events.length) {
      return;
    }

    const eventToEdit = events.find((eventRecord) => normalizeEventId(eventRecord.eventId) === normalizeEventId(initialEditEventId));
    if (eventToEdit && editingEventId !== eventToEdit.eventId) {
      handleEdit(eventToEdit);
    }
  }, [editingEventId, events, handleEdit, initialEditEventId]);

  const handleHeroImageSelection = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    setIsPreparingHeroImage(true);
    try {
      const preparedFile = await prepareImageFileForUpload(file, {
        forceOptimization: true,
        maxDimension: 2200,
        quality: 0.88,
        maxBytes: EVENT_HERO_MAX_IMAGE_UPLOAD_BYTES,
        timeoutMs: EVENT_HERO_IMAGE_PREP_TIMEOUT_MS,
      });
      const uploadFormData = new FormData();
      uploadFormData.append("heroImage", preparedFile);

      const { data } = await axios.post(`${server}/user/event/admin/hero-image`, uploadFormData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.heroImageUrl) {
        handleFormChange("heroImageUrl", data.heroImageUrl);
        toast.success("Hero image selected.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || getImagePreparationErrorMessage(error));
    } finally {
      setIsPreparingHeroImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);

    const venueValidationMessage = validateAdminEventVenueForm(form);
    if (venueValidationMessage) {
      toast.error(venueValidationMessage);
      setIsSaving(false);
      return;
    }

    const payload = {
      eventId: form.eventId,
      title: form.title,
      venue: form.venue,
      venueAddress: form.venueAddress,
      venueLocation: form.venueLocation,
      venueCategory: form.venueCategory,
      venueRating: form.venueRating,
      venueReviewCount: form.venueReviewCount,
      venueHours: form.venueHours,
      venueWebsite: form.venueWebsite,
      venuePhone: form.venuePhone,
      venueLatitude: form.venueLatitude,
      venueLongitude: form.venueLongitude,
      schedule: form.schedule,
      summary: form.summary,
      startsAt: combineLocalDateAndTime(form.startsDate, form.startsTime),
      endsAt: combineLocalDateAndTime(form.endsDate, form.endsTime, form.startsDate, true),
      heroImageUrl: form.heroImageUrl,
      status: form.status,
      displayOnLe: Boolean(form.displayOnLe),
      displayOnPrimary: Boolean(form.displayOnPrimary),
    };

    if (!payload.displayOnLe && !payload.displayOnPrimary) {
      toast.error("Choose LE, primary platform, or both.");
      setIsSaving(false);
      return;
    }

    try {
      const response = editingEventId
        ? await axios.put(`${server}/user/event/admin/events/${encodeURIComponent(editingEventId)}`, payload, { withCredentials: true })
        : await axios.post(`${server}/user/event/admin/events`, payload, { withCredentials: true });

      const savedEvent = response.data.event;
      toast.success(editingEventId ? "Event updated." : "Event created.");
      resetForm();
      await loadEvents();
      if (onEventsChanged) {
        await onEventsChanged();
      }
      if (savedEvent?.eventId) {
        onSelectEvent(savedEvent.eventId);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save event.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (eventRecord) => {
    setOpenMenuEventId("");
    if (!window.confirm(`Archive ${eventRecord.title}?`)) {
      return;
    }

    try {
      await axios.delete(`${server}/user/event/admin/events/${encodeURIComponent(eventRecord.eventId)}`, { withCredentials: true });
      toast.success("Event archived.");
      await loadEvents();
      if (onEventsChanged) {
        await onEventsChanged();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to archive event.");
    }
  };

  const handleCopy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied.`);
    } catch (error) {
      toast.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  return (
    <section className="event-le-admin-card">
      <div className="event-le-admin-header">
        <div>
          <span>Event Admin</span>
          <h3>{editingEventId ? "Edit Event" : "Create Event"}</h3>
        </div>
        <button type="button" className="event-le-small-button" onClick={resetForm}>New</button>
      </div>

      <form className="event-le-form event-le-admin-form" onSubmit={handleSubmit}>
        <label>
          Event Title
          <input value={form.title} onChange={(event) => handleFormChange("title", event.target.value)} required />
        </label>
        <label>
          Event ID
          <input value={form.eventId} onChange={(event) => handleFormChange("eventId", normalizeEventId(event.target.value))} required inputMode="text" autoCapitalize="characters" />
        </label>
        <label>
          Venue
          <input value={form.venue} onChange={(event) => handleFormChange("venue", event.target.value)} />
        </label>
        <label>
          Event Address
          <input value={form.venueAddress} onChange={(event) => handleFormChange("venueAddress", event.target.value)} placeholder="Street, city, state, ZIP" />
          <span className="event-le-field-note">Required unless both latitude and longitude are provided.</span>
        </label>
        <div className="event-le-time-grid event-le-location-grid">
          <label>
            Venue Latitude
            <input type="text" inputMode="decimal" value={form.venueLatitude} onChange={(event) => handleFormChange("venueLatitude", event.target.value)} placeholder="25.7558056" />
          </label>
          <label>
            Venue Longitude
            <input type="text" inputMode="decimal" value={form.venueLongitude} onChange={(event) => handleFormChange("venueLongitude", event.target.value)} placeholder="-80.3665438" />
          </label>
        </div>
        <span className="event-le-field-note">Coordinates are optional when an address is filled in, but both latitude and longitude must be present if you use them.</span>
        <div className="event-le-time-grid">
          <label>
            Club Category
            <input value={form.venueCategory} onChange={(event) => handleFormChange("venueCategory", event.target.value)} placeholder="Firearms academy" />
          </label>
          <label>
            Venue Location
            <input value={form.venueLocation} onChange={(event) => handleFormChange("venueLocation", event.target.value)} placeholder="Floor 1 - University Center North" />
          </label>
          <label>
            Rating
            <input type="text" inputMode="decimal" value={form.venueRating} onChange={(event) => handleFormChange("venueRating", event.target.value)} placeholder="4.9" />
          </label>
          <label>
            Review Count
            <input type="text" inputMode="numeric" value={form.venueReviewCount} onChange={(event) => handleFormChange("venueReviewCount", event.target.value)} placeholder="321" />
          </label>
          <label>
            Hours
            <input value={form.venueHours} onChange={(event) => handleFormChange("venueHours", event.target.value)} placeholder="Open 24 hours" />
          </label>
          <label>
            Website
            <input value={form.venueWebsite} onChange={(event) => handleFormChange("venueWebsite", event.target.value)} placeholder="miamishootersclub.com" />
          </label>
        </div>
        <label>
          Phone
          <input value={form.venuePhone} onChange={(event) => handleFormChange("venuePhone", event.target.value)} placeholder="(305) 551-0100" />
        </label>
        <label>
          Schedule Note
          <input value={form.schedule} onChange={(event) => handleFormChange("schedule", event.target.value)} placeholder="Optional note shown when date/time is not set" />
          <span className="event-le-field-note">Start and end date/time drive the event schedule. Use this only for an extra note or if the date is still pending.</span>
        </label>
        <label>
          Summary
          <input value={form.summary} onChange={(event) => handleFormChange("summary", event.target.value)} />
        </label>
        <div className="event-le-time-grid">
          <label>
            Start Date
            <input type="date" value={form.startsDate} onChange={(event) => handleFormChange("startsDate", event.target.value)} />
          </label>
          <label>
            Start Time
            <input type="time" value={form.startsTime} onChange={(event) => handleFormChange("startsTime", event.target.value)} />
          </label>
          <label>
            End Date
            <input type="date" value={form.endsDate} onChange={(event) => handleFormChange("endsDate", event.target.value)} />
          </label>
          <label>
            End Time
            <input type="time" value={form.endsTime} onChange={(event) => handleFormChange("endsTime", event.target.value)} />
          </label>
        </div>

        <div className="event-le-upload-field">
          <span>Hero Image</span>
          <label className="event-le-upload-button">
            <FiUpload />
            {isPreparingHeroImage ? "Preparing..." : form.heroImageUrl ? "Replace Image" : "Upload Image"}
            <input type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={handleHeroImageSelection} disabled={isPreparingHeroImage} />
          </label>
          {form.heroImageUrl ? (
            <img className="event-le-upload-preview" src={form.heroImageUrl} alt="Event hero preview" />
          ) : null}
        </div>

        <div className="event-le-channel-picker">
          <label>
            <input type="checkbox" checked={form.displayOnLe} onChange={(event) => handleFormChange("displayOnLe", event.target.checked)} />
            Display on GameBird
          </label>
          <label>
            <input type="checkbox" checked={form.displayOnPrimary} onChange={(event) => handleFormChange("displayOnPrimary", event.target.checked)} />
            Display on primary platform
          </label>
        </div>

        <div className="event-le-form-actions">
          <button className="event-le-primary-action" type="submit" disabled={isSaving || isPreparingHeroImage}>
            {isSaving ? "Saving..." : editingEventId ? "Update Event" : "Create Event"}
          </button>
          {editingEventId ? (
            <button className="event-le-secondary-action" type="button" onClick={resetForm}>Cancel</button>
          ) : null}
        </div>
      </form>

      {showEventList ? (
        <div className="event-le-admin-list">
          {isLoading ? <p>Loading events...</p> : null}
          {events.map((eventRecord) => (
            <article key={eventRecord.eventId} className={`event-le-admin-event ${currentEventId === eventRecord.eventId ? "selected" : ""}`}>
              <button type="button" className="event-le-admin-event-main" onClick={() => onSelectEvent(eventRecord.eventId)}>
                <strong>{eventRecord.title}</strong>
                <span>{eventRecord.eventId}</span>
                <span>{eventRecord.displayOnLe ? "GameBird" : "Hidden"} / {eventRecord.displayOnPrimary ? "Primary" : "No Primary"}</span>
              </button>

              <div className="event-le-admin-event-actions">
                {eventRecord.displayOnLe && eventRecord.eventUrl ? (
                  <button type="button" onClick={() => handleCopy(eventRecord.eventUrl, "Event URL")} aria-label="Copy event URL">
                    <FiCopy />
                  </button>
                ) : null}
                <button type="button" onClick={() => setOpenMenuEventId(openMenuEventId === eventRecord.eventId ? "" : eventRecord.eventId)} aria-label="Event menu">
                  <FiMoreVertical />
                </button>
                {openMenuEventId === eventRecord.eventId ? (
                  <div className="event-le-admin-menu">
                    <button type="button" onClick={() => handleEdit(eventRecord)}><FiEdit3 /> Edit</button>
                    <button type="button" onClick={() => handleArchive(eventRecord)}><FiTrash2 /> Archive</button>
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const EventPassPage = () => {
  const eventId = useEventId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state.user.user);
  const [eventIdentity, setEventIdentity] = useState(null);
  const [activeTab, setActiveTab] = useState("pass");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState(() => buildEventProfileEditForm());
  const [passwordForm, setPasswordForm] = useState(() => buildPasswordEditForm());
  const [isAccountEditing, setIsAccountEditing] = useState(false);
  const [cropMode, setCropMode] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const attendee = eventIdentity?.attendee || reduxUser || {};
  const scan = eventIdentity?.scan || {};
  const avatarUrl = attendee.avatar?.url || DEFAULT_AVATAR_URL;
  const bannerUrl = attendee.userBanner?.url || DEFAULT_BANNER_URL;
  const personalQrCodeUrl = scan.eventQrCodeUrl || "";

  const refreshEventPass = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${server}/user/event/pass`, {
        withCredentials: true,
        params: { eventId },
      });
      setEventIdentity(data);
      setEditForm(buildEventProfileEditForm(data.attendee));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load event pass.");
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    refreshEventPass();
  }, [refreshEventPass]);

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (["pass", "about", "account"].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams]);

  const handleImageSelection = async (event, mode) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      setSelectedFile(preparedFile);
      setCropMode(mode);
    } catch (error) {
      toast.error(getImagePreparationErrorMessage(error));
    }
  };

  const saveImage = async (croppedFile) => {
    const mode = cropMode;
    setCropMode(null);

    if (!mode || !croppedFile) {
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append(mode === "avatar" ? "avatar" : "userBanner", croppedFile);

      const endpoint = mode === "avatar" ? "/user/update-avatar" : "/user/update-banner";
      const { data } = await axios.put(`${server}${endpoint}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.user) {
        dispatch(setUser(data.user));
        cacheAuthUser(data.user);
      }

      toast.success(mode === "avatar" ? "Avatar updated." : "Hero updated.");
      await refreshEventPass();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update image.");
    } finally {
      setIsSaving(false);
      setSelectedFile(null);
    }
  };

  const handleProfileUpdate = async (event) => {
    event.preventDefault();

    setIsSaving(true);
    try {
      const { data } = await axios.put(`${server}/user/event/profile`, editForm, { withCredentials: true });

      if (data.user) {
        dispatch(setUser(data.user));
        cacheAuthUser(data.user);
      }

      toast.success("Profile updated.");
      await refreshEventPass();
      setIsAccountEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update profile.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();

    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Complete all password fields.");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${server}/user/update-user-password`, passwordForm, { withCredentials: true });
      setPasswordForm(buildPasswordEditForm());
      toast.success("Password updated.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update password.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelProfileEdit = () => {
    setEditForm(buildEventProfileEditForm(attendee));
    setPasswordForm(buildPasswordEditForm());
    setIsAccountEditing(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${server}/user/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Local session cleanup is enough for the LE surface if the network is unavailable.
    }

    clearAuthSession();
    setStoredCurrentProfile("User");
    navigate(eventPath("/event/login", eventId), { replace: true });
  };

  const renderTabContent = () => {
    if (activeTab === "about") {
      return (
        <div className="event-le-about-grid">
          <div className="event-le-about-tile">
            <span>Event</span>
            <strong>{eventId}</strong>
          </div>
          <div className="event-le-about-tile">
            <span>Pass holder</span>
            <strong>{attendee.handle ? `@${attendee.handle}` : "Guest"}</strong>
          </div>
          <div className="event-le-about-copy">
            <h2>GameBird</h2>
            <p>Your profile, pass QR, RSVP status, maps, and event access stay inside this compact event app.</p>
          </div>
        </div>
      );
    }

    if (activeTab === "account") {
      return (
        <div className="event-le-account-screen">
          <section className="event-le-settings-panel">
            <div className="event-le-settings-heading">
              <div>
                <span>Account Card</span>
                <h3>Profile Info</h3>
              </div>
              {!isAccountEditing ? (
                <button type="button" className="event-le-icon-action" aria-label="Edit account info" onClick={() => setIsAccountEditing(true)}>
                  <FiEdit3 />
                </button>
              ) : null}
            </div>

            {isAccountEditing ? (
              <form className="event-le-form event-le-edit-form" onSubmit={handleProfileUpdate}>
                <label>
                  Name
                  <input value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} required />
                </label>
                <label>
                  Handle
                  <input value={editForm.handle} onChange={(event) => setEditForm({ ...editForm, handle: normalizeHandleInput(event.target.value) })} required inputMode="text" autoCapitalize="none" />
                </label>
                <label>
                  Email
                  <input type="email" value={editForm.email} onChange={(event) => setEditForm({ ...editForm, email: event.target.value })} required />
                </label>
                <label>
                  Phone
                  <input value={editForm.phoneNumber} onChange={(event) => setEditForm({ ...editForm, phoneNumber: event.target.value })} />
                </label>
                <div className="event-le-form-actions">
                  <button className="event-le-primary-action" type="submit" disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Info"}
                  </button>
                  <button className="event-le-secondary-action" type="button" onClick={handleCancelProfileEdit}>Cancel</button>
                </div>
              </form>
            ) : (
              <div className="event-le-account-info-card">
                <div><span>Name</span><strong>{attendee.name || "Event Guest"}</strong></div>
                <div><span>Handle</span><strong>{attendee.handle ? `@${attendee.handle}` : "Not set"}</strong></div>
                <div><span>Email</span><strong>{attendee.email || "Not set"}</strong></div>
                <div><span>Phone</span><strong>{attendee.phoneNumber || "Not set"}</strong></div>
              </div>
            )}
          </section>

          <section className="event-le-settings-panel">
            <div className="event-le-settings-heading">
              <div>
                <span>Password Bento</span>
                <h3>Password</h3>
              </div>
            </div>
            <form className="event-le-form event-le-password-form" onSubmit={handlePasswordUpdate}>
              <label>
                Current Password
                <input type="password" value={passwordForm.oldPassword} onChange={(event) => setPasswordForm({ ...passwordForm, oldPassword: event.target.value })} autoComplete="current-password" />
              </label>
              <label>
                New Password
                <input type="password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })} autoComplete="new-password" />
              </label>
              <label>
                Confirm New Password
                <input type="password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })} autoComplete="new-password" />
              </label>
              <button className="event-le-secondary-action" type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Password"}
              </button>
            </form>
          </section>
        </div>
      );
    }

    return (
      <div className="event-le-pass-stack">
        <div className="event-le-pass-summary">
          <div>
            <span>Your Pass</span>
            <strong>{attendee.name || "Event Guest"}</strong>
            <p>@{attendee.handle || "handle"}</p>
            {attendee.uniqueId ? <small>{attendee.uniqueId}</small> : null}
          </div>
          <button type="button" className="event-le-icon-action" aria-label="Account settings" onClick={() => setActiveTab("account")}>
            <FiSettings />
          </button>
        </div>

        <div className="event-le-qr-card event-le-personal-qr-card">
          {personalQrCodeUrl ? <img src={personalQrCodeUrl} alt="GameBird event pass QR code" /> : null}
          <div>
            <span>GameBird Pass QR</span>
            <strong>@{attendee.handle || "handle"}</strong>
            <p>{attendee.uniqueId || "GameBird ID"}</p>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <EventShell eyebrow="GameBird" title="Your Pass">
      <section className="event-le-pass-card">
        <div className="event-le-hero" style={{ backgroundImage: `url(${bannerUrl})` }}>
          <label className="event-le-image-button">
            <FiCamera />
            <input type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={(event) => handleImageSelection(event, "banner")} />
          </label>

          <div className="event-le-identity-row">
            <div className="event-le-avatar-wrap">
              <img src={avatarUrl} alt={`${attendee.name || "User"} avatar`} />
              <label className="event-le-avatar-edit">
                <FiCamera />
                <input type="file" accept={IMAGE_ACCEPT_WITH_HEIC} onChange={(event) => handleImageSelection(event, "avatar")} />
              </label>
            </div>
            <div className="event-le-identity-copy">
              <h2>{attendee.name || "Event Guest"}</h2>
              <p>@{attendee.handle || "handle"}</p>
            </div>
          </div>
        </div>

        <EventTopTabs activeTab={activeTab} eventId={eventId} localTabs={["pass", "about", "account"]} onLocalTab={setActiveTab} />

        <div className="event-le-tab-panel">{renderTabContent()}</div>

        <button type="button" className="event-le-logout-button" onClick={handleLogout}>Sign Out</button>
      </section>

      {cropMode && selectedFile ? (
        <ImageCropperModal
          updateAvatar={saveImage}
          closeModal={() => {
            setCropMode(null);
            setSelectedFile(null);
          }}
          initialImage={selectedFile}
          aspectRatio={cropMode === "avatar" ? 1 : 4}
          minWidth={cropMode === "avatar" ? 240 : 640}
          isAvatar={cropMode === "avatar"}
          isPost={false}
          isLogo={false}
        />
      ) : null}
    </EventShell>
  );
};

export {
  EventActivationPage,
  EventCheckInPage,
  EventClubPage,
  EventLandingPage,
  EventLoginPage,
  EventPassPage,
  EventProtectedRoute,
  EventSignupPage,
};